import { formatDateTR, formatDateTime } from '../utils/dateUtils';
import { Chip, Box, Typography, Avatar, Stack, Popover, Divider, Tooltip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { warningKeyText } from '../utils/revenueWarnings';

// Mime/extension bazli dosya tipi ikonu ve rengi. Doküman Bankası tablosu
// icin kullaniliyor — hem "dosya tipi" hucresinde hem de kart-tarzi
// gorunumlerde ayni mapping'i cagirabilirsiniz.
export const getDocumentFileTypeMeta = (mimeOrName) => {
  const val = (mimeOrName || '').toLowerCase();
  const isPdf = val.includes('pdf') || val.endsWith('.pdf');
  const isDoc = val.includes('word') || val.endsWith('.doc') || val.endsWith('.docx');
  const isXls = val.includes('sheet') || val.includes('excel')
    || val.endsWith('.xls') || val.endsWith('.xlsx') || val.endsWith('.csv');
  const isVideo = val.startsWith('video/') || val.endsWith('.mp4')
    || val.endsWith('.mov') || val.endsWith('.avi') || val.endsWith('.mkv');
  const isImage = val.startsWith('image/') || val.endsWith('.png')
    || val.endsWith('.jpg') || val.endsWith('.jpeg') || val.endsWith('.gif')
    || val.endsWith('.webp') || val.endsWith('.svg');

  if (isPdf) return { Icon: PictureAsPdfIcon, color: '#e53935', label: 'PDF' };
  if (isDoc) return { Icon: DescriptionIcon, color: '#1e88e5', label: 'DOC' };
  if (isXls) return { Icon: TableChartIcon, color: '#43a047', label: 'XLS' };
  if (isVideo) return { Icon: PlayCircleFilledIcon, color: '#8e24aa', label: 'VIDEO' };
  if (isImage) return { Icon: ImageIcon, color: '#fb8c00', label: 'IMG' };
  return { Icon: InsertDriveFileIcon, color: '#78909c', label: 'FILE' };
};

// TL gosterimi: 7200 -> "7.200 ₺"; 7200.5 -> "7.200,50 ₺"; tam sayilarda
// ".00" cekligini gizle.
const formatTL = (n) => {
  const num = Number(n || 0);
  const hasDecimals = num % 1 !== 0;
  return `${num.toLocaleString('tr-TR', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  })} ₺`;
};

// Aktarim gecmisini Popover'da gosteren tiklanabilir hucre. Hucreye basinca
// tum transfer_logs listesi acilir: Q? -> Q?, aktaran kisi, tarih ve not.
const TransferHistoryCell = ({ logs }) => {
  const safeLogs = Array.isArray(logs) ? logs : [];
  const [anchorEl, setAnchorEl] = React.useState(null);
  if (safeLogs.length === 0) return '-';
  const last = safeLogs[safeLogs.length - 1];
  const extra = safeLogs.length > 1 ? ` (+${safeLogs.length - 1})` : '';
  const personLabel = last?.transferred_by_user
    ? `${last.transferred_by_user.first_name || ''} ${last.transferred_by_user.last_name || ''}`.trim()
    : '';
  const open = Boolean(anchorEl);
  return (
    <>
      <Box
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        sx={{
          display: 'flex', flexDirection: 'column', gap: 0.25,
          cursor: 'pointer', borderRadius: 1, p: 0.5, mx: -0.5,
          transition: 'background 160ms ease',
          '&:hover': { background: '#f4f5f7' },
        }}
      >
        <Chip
          onClick={() => {}}
          size="small"
          icon={<SwapHorizIcon sx={{ fontSize: 14, ml: '6px !important' }} />}
          label={`Q${last.from_quarter} → Q${last.to_quarter}`}
          color="primary"
          sx={{ height: 22, fontWeight: 600, alignSelf: 'flex-start',
                '& .MuiChip-label': { px: 0.75 } }}
        />
        {personLabel && (
          <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
            {personLabel}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {last.transferred_at ? formatDateTR(last.transferred_at) : ''}{extra}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: {
          mt: 0.5, p: 0, minWidth: 320, maxWidth: 420,
          borderRadius: '12px', boxShadow: '0 8px 32px rgba(20,30,55,.12)',
          border: '1px solid #e8eaee',
        } } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e8eaee',
                   display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ fontSize: 18, color: '#3a5af0' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '13.5px' }}>
            Aktarım Geçmişi
          </Typography>
          <Box sx={{ ml: 'auto', fontSize: '11.5px', color: '#8b95a5' }}>
            {safeLogs.length} kayıt
          </Box>
        </Box>
        <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
          {[...safeLogs].reverse().map((log, idx) => {
            const who = log?.transferred_by_user
              ? `${log.transferred_by_user.first_name || ''} ${log.transferred_by_user.last_name || ''}`.trim()
              : '—';
            return (
              <React.Fragment key={log.id || idx}>
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      onClick={() => {}}
                      size="small"
                      icon={<SwapHorizIcon sx={{ fontSize: 13, ml: '6px !important' }} />}
                      label={`Q${log.from_quarter} → Q${log.to_quarter}`}
                      color="primary"
                      sx={{ height: 20, fontWeight: 600,
                            '& .MuiChip-label': { px: 0.75, fontSize: '11px' } }}
                    />
                    <Typography variant="caption" sx={{ color: '#8b95a5', ml: 'auto' }}>
                      {log.transferred_at ? formatDateTR(log.transferred_at) : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#1f2733' }}>
                    {who}
                  </Typography>
                  {log.note && (
                    <Typography variant="caption" sx={{
                      display: 'block', mt: 0.5, color: '#626b78', fontStyle: 'italic',
                      whiteSpace: 'pre-wrap',
                    }}>
                      “{log.note}”
                    </Typography>
                  )}
                </Box>
                {idx < safeLogs.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

// Inline status chip — render callback'i `t`'yi kararli sekilde alamadigi
// icin (DataTable cagrim variant'lari farkli imza kullanabiliyor), hook'u
// burada inline component icinde tuketiyoruz.
const RevenueStatusChip = ({ value }) => {
  const { t } = useTranslation();
  const CONFIG = {
    PENDING:  { key: 'userRevenueEntries.status.pending',  color: 'warning' },
    APPROVED: { key: 'userRevenueEntries.status.approved', color: 'success' },
    REJECTED: { key: 'userRevenueEntries.status.rejected', color: 'error' },
  };
  const cfg = CONFIG[value] || { key: '', color: 'default' };
  const label = cfg.key ? t(cfg.key) : (value || '-');
  return <Chip onClick={() => {}} label={label} size="small" color={cfg.color} sx={{ height: 22, fontWeight: 600 }} />;
};

// Satira ait yesil/sari uyarilarin chip listesi. Satira enrichRevenueEntries
// tarafindan eklenen `warningsKeys` (string[] i18n key'leri) okunur. Sadece
// "uygun" varsa tek yesil chip; baska uyari varsa onlar amber chip listesi
// (uygun gosterilmez).
const RevenueWarningsCell = ({ keys }) => {
  const { t } = useTranslation();
  const list = Array.isArray(keys) ? keys : [];
  if (list.length === 0) return '-';
  const onlyUygun = list.length === 1 && list[0].endsWith('.uygun');
  if (onlyUygun) {
    return (
      <Chip
      onClick={() => {}}
        label={t(list[0])}
        size="small"
        color="success"
        sx={{ height: 22, fontWeight: 600 }}
      />
    );
  }
  // Cogu uyari — kompakt amber chip listesi, wrap eder. Chip metni
  // hucreye sigmiyorsa ellipsizlenir; Tooltip uzerine gelince tam metni gosterir.
  const realWarnings = list.filter((k) => !k.endsWith('.uygun'));
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {realWarnings.map((k) => {
        const text = warningKeyText(k, t);
        return (
          <Tooltip key={k} title={text} placement="top" arrow>
            <Chip
              onClick={() => {}}
              label={text}
              size="small"
              sx={{
                height: 20,
                maxWidth: 180,
                fontSize: '11px',
                fontWeight: 600,
                bgcolor: 'rgba(224,161,6,0.12)',
                color: '#a06b00',
                border: '1px solid rgba(224,161,6,0.3)',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export const formatCurrency = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n || 0);

const stringToColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 55%, 40%)`;
};

export const TABLE_COLUMNS = {
  users: [
    { field: 'firstName', headerName: 'table.columns.firstName', flex: 1.5 },
    { field: 'lastName', headerName: 'table.columns.lastName', flex: 1.5 },
    { field: 'email', headerName: 'table.columns.email', flex: 2.5 },
    {
      field: 'role',
      headerName: 'table.columns.role',
      flex: 1.5,
      render: (value, row, rowIndex, t) => {
        const roleColors = {
          ROOT: 'error',
          DIRECTOR: 'primary',
          GROUP_MANAGER: 'secondary',
          TEAM_MANAGER: 'info',
          TEAM_MEMBER: 'default',
        };
        return (
          <Chip
          onClick={() => {}}
            label={t ? t(`table.values.roles.${value}`) : value}
            color={roleColors[value] || 'default'}
            size="small"
            variant="filled"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: '80px',
            }}
          />
        );
      },
      i18n: true,
    },
    { field: 'companyName', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'teamName', headerName: 'table.columns.team', flex: 1.5 },
    { field: 'firmCount', headerName: 'table.columns.firmCount', flex: 1, render: (value) => value ?? 0 },
  ],

  groups: [
    { field: 'name', headerName: 'table.columns.groupName', flex: 2 },
    { field: 'description', headerName: 'table.columns.description', flex: 3 },
    { field: 'managerName', headerName: 'table.columns.groupManager', flex: 2 },
    { field: 'companyName', headerName: 'table.columns.company', flex: 2, showIfRoot: true },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  companies: [
    { field: 'name', headerName: 'table.columns.companyName', flex: 3 },
    {
      field: 'name_of_representative_',
      headerName: 'table.columns.representativeName',
      flex: 2.5,
    },
    {
      field: 'email_address_of_representative',
      headerName: 'table.columns.representativeEmail',
      flex: 3,
    },
    {
      field: 'phone_of_representative',
      headerName: 'table.columns.representativePhone',
      flex: 2,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  teams: [
    { field: 'name', headerName: 'table.columns.teamName', flex: 1.5 },
    { field: 'companyName', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'groupName', headerName: 'table.columns.group', flex: 1.5 },
    { field: 'managerName', headerName: 'table.columns.teamManager', flex: 2 },
    { field: 'description', headerName: 'table.columns.description', flex: 2 },
    {
      field: 'memberCount',
      headerName: 'table.columns.memberCount',
      flex: 1.5,
    },
    {
      field: 'firmCount',
      headerName: 'table.columns.firmCount',
      flex: 1.5,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  paymentPlanTemplates: [
    { field: 'templateName', headerName: 'paymentPlanTemplates.table.templateName', flex: 2 },
    { field: 'description', headerName: 'paymentPlanTemplates.table.description', flex: 3 },
    { field: 'year', headerName: 'paymentPlanTemplates.table.year', flex: 1 },
    { field: 'companyName', headerName: 'table.columns.company', flex: 2, showIfRoot: true },
    {
      field: 'status',
      headerName: 'scopeOfWork.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  pricingProcess: [
    { field: 'processName', headerName: 'pricingProcess.table.processName', flex: 2 },
    { field: 'description', headerName: 'pricingProcess.table.description', flex: 3 },
    { field: 'companyName', headerName: 'table.columns.company', flex: 2, showIfRoot: true },
    {
      field: 'status',
      headerName: 'scopeOfWork.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  teamMembers: [
    { field: 'firstName', headerName: 'table.columns.firstName', flex: 1.5 },
    { field: 'lastName', headerName: 'table.columns.lastName', flex: 1.5 },
    { field: 'email', headerName: 'table.columns.email', flex: 2.5 },
    { field: 'phoneNumber', headerName: 'table.columns.phone', flex: 1.5 },
    { field: 'titleName', headerName: 'table.columns.title', flex: 2 },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: value => new Date(value).toLocaleDateString('tr-TR'),
    },
  ],

  teamFirms: [
    { field: 'firm_name', headerName: 'firms.table.firmName', flex: 2 },
    { field: 'firm_short_name', headerName: 'firms.table.firmShortName', flex: 1.5 },
    { field: 'firm_city', headerName: 'firms.table.firmCity', flex: 1.5 },
  ],

  userFirms: [
    { field: 'firm_name', headerName: 'firms.table.firmName', flex: 2 },
    { field: 'firm_short_name', headerName: 'firms.table.firmShortName', flex: 1.5 },
    { field: 'firm_city', headerName: 'firms.table.firmCity', flex: 1.5 },
  ],

  titles: [
    { field: 'title_name', headerName: 'table.columns.titleName', flex: 2 },
    { field: 'title_code', headerName: 'table.columns.code', width: 120 },
    {
      field: 'title_description',
      headerName: 'table.columns.description',
      flex: 3,
    },
    {
      field: 'title_status',
      headerName: 'table.columns.status',
      width: 100,
      render: value =>
        value ? 'table.values.status.active' : 'table.values.status.inactive',
      i18n: true,
    },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      width: 130,
      render: formatDateTR,
    },
  ],

  leaveTypes: [
    { field: 'leave_type_name', headerName: 'leaveTypes.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'leave_type_description',
      headerName: 'leaveTypes.table.description',
      flex: 2,
    },
    {
      field: 'duration',
      headerName: 'leaveTypes.table.duration',
      flex: 1.5,
      render: (value, row, rowIndex, t) => {
        const parts = [];
        if (row.leave_type_days && row.leave_type_days > 0) {
          const daysLabel = row.leave_type_days === 1 
            ? (t ? t('leaveTypes.table.day') : 'gün')
            : (t ? t('leaveTypes.table.days') : 'gün');
          parts.push(`${row.leave_type_days} ${daysLabel}`);
        }
        if (row.leave_type_hours && row.leave_type_hours > 0) {
          const hoursLabel = row.leave_type_hours === 1
            ? (t ? t('leaveTypes.table.hour') : 'saat')
            : (t ? t('leaveTypes.table.hours') : 'saat');
          parts.push(`${row.leave_type_hours} ${hoursLabel}`);
        }
        return parts.length > 0 ? parts.join(' ') : '-';
      },
    },
    {
      field: 'leave_type_deduct_from_annual_leave',
      headerName: 'leaveTypes.table.deductFromAnnualLeave',
      flex: 1,
      render: (value, row, rowIndex, t) => {
        if (t) {
          return value ? t('leaveTypes.table.yes') : t('leaveTypes.table.no');
        }
        return value ? 'Evet' : 'Hayır';
      },
    },
    {
      field: 'leave_type_status',
      headerName: 'leaveTypes.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  partners: [
    { field: 'partner_name', headerName: 'partners.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'partner_description',
      headerName: 'partners.table.description',
      flex: 2,
    },
    {
      field: 'partner_status',
      headerName: 'partners.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  sectors: [
    { field: 'sector_name', headerName: 'sectors.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'sector_description',
      headerName: 'sectors.table.description',
      flex: 2,
    },
    {
      field: 'sector_status',
      headerName: 'sectors.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  contractSubjects: [
    { field: 'name', headerName: 'contractSubjects.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'description',
      headerName: 'contractSubjects.table.description',
      flex: 2,
    },
    {
      field: 'isProjectBased',
      headerName: 'contractSubjects.table.isProjectBased',
      flex: 1.25,
      render: value => value == true ? 'Evet' : 'Hayır',
      i18n: true,
    },
    {
      field: 'status',
      headerName: 'contractSubjects.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  jobTypes: [
    { field: 'name', headerName: 'jobTypes.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'description',
      headerName: 'jobTypes.table.description',
      flex: 2,
    },
    {
      field: 'isProjectBasedText',
      headerName: 'jobTypes.table.isProjectBased',
      flex: 1.25,
    },
    {
      field: 'statusGroup',
      headerName: 'jobTypes.table.statusGroup',
      flex: 1.25,
      render: (value, row, rowIndex, t) =>
        t ? t(`jobTypes.statusGroups.${value}`) : value,
    },
    {
      field: 'status',
      headerName: 'jobTypes.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  scopeOfWork: [
    { field: 'name', headerName: 'scopeOfWork.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'description',
      headerName: 'scopeOfWork.table.description',
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'scopeOfWork.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  pricingMethods: [
    { field: 'name', headerName: 'pricingMethods.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'description',
      headerName: 'pricingMethods.table.description',
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'pricingMethods.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  workStatus: [
    { field: 'name', headerName: 'workStatus.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'description',
      headerName: 'workStatus.table.description',
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'workStatus.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  reportTypes: [
    { field: 'report_type_name', headerName: 'reportTypes.table.name', flex: 2 },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'tags',
      headerName: 'reportTypes.table.tags',
      flex: 2,
      render: (value, row) => {
        const tags = row?.tags || [];
        if (!tags.length) return '-';
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tags.map((tag, idx) => {
              const label = typeof tag === 'string' ? tag : (tag?.name ?? '-');
              return (
                <Chip
                  onClick={() => {}}
                  key={typeof tag === 'string' ? tag : tag?.id ?? idx}
                  label={label}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'report_type_description',
      headerName: 'reportTypes.table.description',
      flex: 2,
    },
    {
      field: 'report_type_status',
      headerName: 'reportTypes.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  tasks: [
    { field: 'companyName', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'firmName', headerName: 'tasks.table.firm', flex: 1.25 },
    { field: 'taskTitle', headerName: 'tasks.table.title', flex: 1 },
    { field: 'taskDescription', headerName: 'tasks.table.description', flex: 2 },
    {
      field: 'taskStartDate',
      headerName: 'tasks.table.startDate',
      flex: 1.30,
      render: formatDateTR,
    },
    {
      field: 'taskDueDate',
      headerName: 'tasks.table.dueDate',
      flex: 1.30,
      render: formatDateTR,
    },
    {
      field: 'taskStatus',
      headerName: 'tasks.table.status',
      flex: 1.4,
      render: (value, row, rowIndex, t) => {
        const statusColors = {
          PENDING: 'warning',
          IN_PROGRESS: 'info',
          COMPLETED: 'success',
          CANCELLED: 'error',
        };
        return (
          <Chip
          onClick={() => {}}
            label={t ? t(`tasks.table.statuses.${value}`) : value}
            color={statusColors[value] || 'default'}
            size="small"
            variant="filled"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: '80px',
            }}
          />
        );
      },
      i18n: true,
    },
    { field: 'createdByUser', headerName: 'tasks.table.createdBy', flex: 1.5 },
  ],

  documentBankTags: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'name', headerName: 'documentBankTags.table.name', flex: 2 },
    {
      field: 'color',
      headerName: 'documentBankTags.table.color',
      flex: 1,
      render: (value) => (
        <Box
          sx={{
            width: 20,
            height: 20,
            backgroundColor: value,
            borderRadius: '50%',
            border: '1px solid #ccc',
          }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  tags: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'name', headerName: 'tags.table.name', flex: 2 },
    {
      field: 'tags_color',
      headerName: 'tags.table.color',
      flex: 1,
      render: (value) => (
        <Box
          sx={{
            width: 20,
            height: 20,
            backgroundColor: value,
            borderRadius: '50%',
            border: '1px solid #ccc',
          }}
        />
      ),
    },
    {
      field: 'tags_page',
      headerName: 'tags.table.page',
      flex: 1.5,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  contracts: [
    {
      field: 'contract_subject_name',
      headerName: 'contracts.table.contractSubject',
      flex: 1.5,
    },
    {
      field: 'scope_of_work_name',
      headerName: 'contracts.table.scopeOfWork',
      flex: 1.5,
    },
    { field: 'firm_name', headerName: 'contracts.table.firm', flex: 1.5 },
    {
      field: 'firm_short_name',
      headerName: 'contracts.table.firmShortName',
      flex: 1.5,
    },
    {
      field: 'project_numbers',
      headerName: 'contracts.table.projectNumbers',
      flex: 1.5,
      render: value => value || '-',
    },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    {
      field: 'contract_signing_date',
      headerName: 'contracts.table.contractSigningDate',
      flex: 1.5,
      sortable: true,
      render: formatDateTR,
    },
    {
      field: 'work_started_without_contract',
      headerName: 'contracts.form.workStartedWithoutContract',
      flex: 1.5,
      render: value => value,
      i18n: true,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      sortable: true,
      render: formatDateTR,
    },
  ],

  proposal_status: [
    {
      field: 'name',
      headerName: 'proposalStatuses.table.name',
      flex: 1.5,
      sortable: true,
    },
    {
      field: 'description',
      headerName: 'proposalStatuses.table.description',
      flex: 2,
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'proposalStatuses.table.status',
      flex: 1,
      sortable: true,
      render: value => (value === 'active' || value === true ? 'Aktif' : 'Pasif'),
    },
    {
      field: 'company_name',
      headerName: 'table.columns.company',
      flex: 1.5,
      sortable: true,
      showIfRoot: true,
      render: value => value || '-',
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      sortable: true,
      render: formatDateTR,
    },
  ],

  offers: [
    {
      field: 'firm_name',
      headerName: 'offers.table.firm',
      flex: 1.5,
      render: value => value || '-',
    },
    {
      field: 'firm_short_name',
      headerName: 'offers.table.firmShortName',
      flex: 1.5,
      render: value => value || '-',
    },
    { 
      field: 'company_name', 
      headerName: 'table.columns.company', 
      flex: 1.5,
      showIfRoot: true,
      render: value => value || '-',
    },
    {
      field: 'contract_subject_name',
      headerName: 'offers.table.contractSubject',
      flex: 1.5,
      render: value => value || '-',
    },
    {
      field: 'proposal_date',
      headerName: 'offers.table.proposalDate',
      flex: 1.5,
      sortable: true,
      render: value => value ? formatDateTR(value) : '-',
    },
    {
      field: 'proposal_status_name',
      headerName: 'offers.table.proposalStatus',
      flex: 1.5,
      render: value => value || '-',
    },
    {
      field: 'created_by_name',
      headerName: 'offers.table.createdBy',
      flex: 1.5,
      render: value => value || '-',
    },
  ],

  jobs: [
    {
      field: 'company_name',
      headerName: 'table.columns.company',
      flex: 1.25,
      showIfRoot: true,
      render: (value, row) => row.company?.name || '-',
    },
    {
      field: 'firm_name',
      headerName: 'firms.table.firmName',
      flex: 1.5,
      render: (value, row) => row.contract?.firm?.firm_name || '-',
    },
    {
      field: 'firm_short_name',
      headerName: 'firms.table.firmShortName',
      flex: 1.25,
      render: (value, row) => row.contract?.firm?.firm_short_name || '-',
    },
    {
      field: 'job_title',
      headerName: 'jobs.table.jobTitle',
      flex: 2,
      render: (value) => value || '-',
    },
    {
      // Tek kolon: TEKNOPARK ise `teknopark_unit`, diger iş türlerinde
      // `project_number`. Kolon basligi is turune gore adapte (defaultValue
      // fallback ile — t() cagrilamadigi zaman "Proje No" gorunur).
      field: 'project_number',
      headerName: 'jobs.table.projectNumber',
      flex: 1.25,
      render: (value, row) => {
        const typeName = row?.job_type_obj?.name || row?.job_type;
        if (typeName === 'TEKNOPARK') {
          return row?.teknopark_unit || row?.teknoparkUnit || '-';
        }
        return value || '-';
      },
    },
    {
      field: 'job_type',
      headerName: 'jobs.table.jobType',
      flex: 1.5,
      render: (value, row, rowIndex, t) => {
        // value, useJobs.transformData icinde job_type_obj || legacy enum
        // wrapper ile doldurulur. FK'li yeni isler dogrudan kullanici tanimli
        // name'i gosterir; eski enum'li isler ise lokalize anahtar (jobs.jobTypes.*)
        // ile cevrilir, boylece transition'da gorunum bozulmaz.
        const name = (value && typeof value === 'object') ? value.name : value;
        const enumKey = row?.job_type_enum || (typeof value === 'object' ? value?.name : value);
        const hasFkRecord = value && typeof value === 'object' && value.id != null;
        if (hasFkRecord) return name || '-';
        if (enumKey && t) {
          const translated = t(`jobs.jobTypes.${enumKey}`);
          // Cevirisi bulunmazsa i18next anahtarin kendisini doner — fallback olarak ham name.
          return translated && translated !== `jobs.jobTypes.${enumKey}` ? translated : (name || enumKey);
        }
        return name || enumKey || '-';
      }
    },
    {
      field: 'job_status',
      headerName: 'jobs.table.jobStatus',
      flex: 1.5,
      render: (value, row, rowIndex, t) => {
        const statusColors = {
          'YAZIMDA': 'warning',
          'DEGERLENDIRMEDE': 'info',
          'ONAYLANDI': 'success',
          'DEVRALINDI': 'primary',
          'REDDEDILDI': 'error',
          'KAPATILDI': 'default',
          'DANISMANLIK_SONLANDIRILDI': 'default'
        };
        return (
          <Chip
          
            label={value ? `${value}` : '-'}
            color={statusColors[value] || 'default'}
            size="small"
            variant="filled"
            onClick={() => {
            }}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: '80px',
            }}
          />
        );
      }
    },
    {
      field: 'createdByUser',
      headerName: 'jobs.table.createdBy',
      flex: 1.5,
      render: (value, row) => {
        if (row.user?.first_name && row.user?.last_name) {
          return `${row.user.first_name} ${row.user.last_name}`;
        }
        return '-';
      }
    },
    {
      field: 'technical_responsible_name',
      headerName: 'jobAssignments.table.technicalUserName',
      flex: 1.5,
      render: (value, row) => row.technical_responsible_name || '-',
    },
    {
      field: 'financial_responsible_name',
      headerName: 'jobAssignments.table.financialUserName',
      flex: 1.5,
      render: (value, row) => row.financial_responsible_name || '-',
    },
    {
      field: 'contract_info',
      headerName: 'jobs.table.contract',
      flex: 2,
      render: (value, row) => {
        if (row.contract) {
          const contract = row.contract;
          const subject = contract.contract_subject?.name || 'N/A';
          return `${subject}`;
        }
        return '-';
      }
    },
    {
      // Sözleşme gelme durumu: contract.work_started_without_contract=true
      // "Geldi" demektir (alan adı legacy — bkz. useContracts transformData).
      // Kolon hem tabloda hem CSV raporunda görünür.
      field: 'contract_received',
      headerName: 'contracts.table.contractReceived',
      flex: 1.25,
      render: (value, row, rowIndex, t) => {
        if (!row.contract) return '-';
        const came = row.contract.work_started_without_contract === true;
        if (t) {
          return came
            ? t('contracts.form.options.came')
            : t('contracts.form.options.didNotCome');
        }
        return came ? 'Geldi' : 'Gelmedi';
      },
    },
    {
      field: 'tagIds',
      headerName: 'jobs.table.tags',
      flex: 2,
      render: (value, row) => {
        if (row.tags && row.tags.length > 0) {
          const maxTags = 2;
          const visibleTags = row.tags.slice(0, maxTags);
          const remainingCount = row.tags.length - maxTags;
          
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {visibleTags.map((tag, index) => (
                <Chip
                  onClick={() => {
                  }}
                  key={tag.id || index}
                  label={tag.name}
                  size="small"
                  sx={{
                    backgroundColor: tag.tags_color,
                    color: '#fff',
                    fontSize: '0.7rem',
                    height: '20px',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              ))}
              {remainingCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    ml: 0.5,
                  }}
                >
                  +{remainingCount}
                </Typography>
              )}
            </Box>
          );
        }
        return '-';
      }
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.25,
      // Hidden from the Jobs table display; kept for search/sort and export.
      hiddenFromTable: true,
      render: formatDateTR,
    },
  ],

  invoices: [
    {
      field: 'company_name',
      headerName: 'table.columns.company',
      flex: 1.25,
      showIfRoot: true,
      render: (value, row) => row.company?.name || '-',
    },
    {
      field: 'invoice_name',
      headerName: 'invoices.table.invoiceName',
      flex: 1.5,
      render: (value) => value || '-',
    },
    {
      field: 'contractInfo.firmName',
      headerName: 'firms.table.firmName',
      flex: 1.5,
      render: (value, row) => row.contractInfo?.firmName || '-',
    },
    {
      field: 'contractInfo.firmShortName',
      headerName: 'firms.table.firmShortName',
      flex: 1.25,
      render: (value, row) => row.contractInfo?.firmShortName || '-',
    },
    {
      field: 'jobInfo.title',
      headerName: 'invoices.table.jobTitle',
      flex: 1.25,
      render: (value, row) => row.jobInfo?.title || '-',
    },
    {
      field: 'pricingProcessInfo.name',
      headerName: 'invoices.table.pricingProcess',
      flex: 1.25,
      render: (value, row) => row.pricingProcessInfo?.name || '-',
    },
    {
      field: 'split_invoice',
      headerName: 'invoices.table.splitInvoice',
      flex: 1,
      render: (value) => value ? 'Evet' : 'Hayır',
    },
    {
      field: 'total_unit_price',
      headerName: 'invoices.table.totalUnitPrice',
      flex: 1.25,
      render: (value) => value ? `₺${parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-',
    },
    {
      field: 'payment_invoice_code_count',
      headerName: 'invoices.table.paymentInvoiceCount',
      flex: 1,
      render: (value, row) => {
        let count = 0;
        if (row.invoice_code_ids && row.invoice_code_ids.length > 0) count++;
        if (row.invoice_code_ids && row.invoice_code_ids.length > 0) {
          count += row.invoice_code_ids.length;
        }
        return count > 0 ? `${count - 1} Kod` : '-';
      },
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.25,
      sortable: true,
      render: formatDateTR,
    },
  ],

  jobAssignments: [
    { field: 'projectNumber', headerName: 'jobs.table.projectNumber', flex: 1.5, sortable: true, render: (value) => value || '-' },
    { field: 'firmName', headerName: 'firms.table.firmName', flex: 1.5, sortable: true },
    { field: 'firmShortName', headerName: 'firms.table.firmShortName', flex: 1.25, sortable: true, render: (value) => value || '-' },
    { field: 'jobType', headerName: 'jobs.table.jobType', flex: 1.5, sortable: true,
      render: (value, row, rowIndex, t) => {
        if (!value) return '-';
        // Dinamik is turleri icin lokalize edilmis label yok; t() raw key dondurur.
        // Eski enum (TUBITAK/AR_GE/...) icin ceviriyi kullan, yoksa ham degeri bas.
        if (!t) return value;
        const key = `jobs.jobTypes.${value}`;
        const translated = t(key);
        return translated === key ? value : translated;
      },
    },
    { field: 'jobStatus', headerName: 'jobAssignments.table.jobStatus', flex: 1.5, sortable: true,
      render: (value) => {
        const statusColors = {
          'YAZIMDA': 'warning', 'DEGERLENDIRMEDE': 'info', 'ONAYLANDI': 'success',
          'DEVRALINDI': 'primary', 'REDDEDILDI': 'error', 'KAPATILDI': 'default'
        };
        return <Chip onClick={() => {}} label={value || '-'} color={statusColors[value] || 'default'} size="small" />;
      }
    },
    { field: 'companyName', headerName: 'jobAssignments.table.company', flex: 1.5, sortable: true, showIfRoot: true },
    { field: 'technicalTeamName', headerName: 'jobAssignments.table.technicalTeamName', flex: 1.5, sortable: true },
    { field: 'technicalUserName', headerName: 'jobAssignments.table.technicalUserName', flex: 1.5, sortable: true },
    { field: 'financialTeamName', headerName: 'jobAssignments.table.financialTeamName', flex: 1.5, sortable: true },
    { field: 'financialUserName', headerName: 'jobAssignments.table.financialUserName', flex: 1.5, sortable: true },
    { field: 'createdByUser', headerName: 'jobAssignments.table.createdBy', flex: 1.5, sortable: true },
    { field: 'assignedByUser', headerName: 'jobAssignments.table.assignedBy', flex: 1.5, sortable: true },
    {
      field: 'warningMessage',
      headerName: 'jobAssignments.table.warning',
      flex: 2.5,
      sortable: false,
      render: (value, row) => {
        if (!row.warningType || row.warningType === 'NONE') return '-';

        const colorMap = {
          TECH_LEFT: '#fff3cd', // warning
          NO_FINANCIAL: '#cfe2ff', // info
          TECH_LEFT_AND_NO_FINANCIAL: '#f8d7da', // error
        };

        const borderMap = {
          TECH_LEFT: '#ffecb5',
          NO_FINANCIAL: '#9ec5fe',
          TECH_LEFT_AND_NO_FINANCIAL: '#f5c2c7',
        };

        const bg = colorMap[row.warningType] || '#f5f5f5';
        const border = borderMap[row.warningType] || '#e0e0e0';

        return (
          <Box
            sx={{
              bgcolor: bg,
              border: '1px solid',
              borderColor: border,
              borderRadius: 1.5,
              px: 1.5,
              py: 0.75,
              fontSize: '0.8rem',
              lineHeight: 1.4,
              color: 'text.primary',
              whiteSpace: 'normal',
            }}
          >
            {value || '-'}
          </Box>
        );
      },
    },
  ],

  firms: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.25, showIfRoot: true },
    {
      field: 'firm_name',
      headerName: 'firms.table.firmName',
      flex: 2,
    },
    {
      field: 'firm_short_name',
      headerName: 'firms.table.firmShortName',
      flex: 1.5,
    },
    {
      field: 'luca_code',
      headerName: 'firms.table.lucaCode',
      flex: 1,
    },
    {
      field: 'isEstablishedText',
      headerName: 'firms.table.isEstablished',
      flex: 1,
    },
    { field: 'firm_city', headerName: 'firms.table.city', flex: 1.25 },
    {
      field: 'partnerReferralText',
      headerName: 'firms.table.partnerReferral',
      flex: 1.5,
    },
    {
      field: 'referenceTypeText',
      headerName: 'firms.table.referenceType',
      flex: 1.5,
    },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  firm_cards: [
    {
      field: 'fullName',
      headerName: 'firms.firmCards.table.fullName',
      flex: 1.5,
    },
    {
      field: 'firm_cart_title',
      headerName: 'firms.firmCards.table.title',
      flex: 1.5,
    },
    {
      field: 'firm_cart_mail',
      headerName: 'firms.firmCards.table.email',
      flex: 2.5,
    },
    {
      field: 'firm_cart_phone',
      headerName: 'firms.firmCards.table.phone',
      flex: 1.5,
    },
    {
      field: 'firm_cart_second_phone',
      headerName: 'firms.firmCards.table.secondPhone',
      flex: 1.5,
    },
    {
      field: 'firm_cart_task',
      headerName: 'firms.firmCards.table.task',
      flex: 1.5,
    },
    {
      field: 'firm_cart_description',
      headerName: 'firms.firmCards.table.description',
      flex: 2,
    },
    { field: 'statusText', headerName: 'firmcard.table.status', flex: 1 },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.5,
      render: formatDateTR,
    },
  ],

  expenseTypes: [
    {
      field: 'expense_type_name',
      headerName: 'expenseTypes.table.name',
      flex: 2,
    },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },

    {
      field: 'expense_type_description',
      headerName: 'expenseTypes.table.description',
      flex: 2,
    },
    {
      field: 'expense_type_status',
      headerName: 'expenseTypes.table.status',
      flex: 1,
      render: value =>
        value === 'active'
          ? 'table.values.status.active'
          : 'table.values.status.inactive',
      i18n: true,
    },
  ],

  invoiceCodes: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'code_name', headerName: 'invoiceCodes.table.codeName', flex: 1.5 },
    { field: 'product_name', headerName: 'invoiceCodes.table.productName', flex: 1.5 },
    { field: 'description', headerName: 'invoiceCodes.table.description', flex: 2 },
    {
      field: 'created_at',
      headerName: 'table.columns.createdAt',
      flex: 1.25,
      render: formatDateTR,
    },
  ],

  leaves: [
    {
      field: 'startDate',
      headerName: 'leaves.table.startDate',
      flex: 1.5,
      render: formatDateTime,
    },
    {
      field: 'endDate',
      headerName: 'leaves.table.endDate',
      flex: 1.5,
      render: formatDateTime,
    },
    { field: 'leaveTypeName', headerName: 'leaves.table.leaveType', flex: 1 },
    { field: 'description', headerName: 'leaves.table.description', flex: 2 },
    { field: 'duration', headerName: 'leaves.table.duration', flex: 1 },
    {
      field: 'status',
      headerName: 'leaves.table.status',
      flex: 1.25,
    },
  ],

  expenses: [
    {
      field: 'user',
      headerName: 'expenses.table.user',
      flex: 1.5,
      render: (value, row) => {
        return row.user ? `${row.user.first_name} ${row.user.last_name}` : '-';
      },
    },
    {
      field: 'expenseTypeName',
      headerName: 'expenses.table.expenseType',
      flex: 1.5,
    },
    {
      field: 'amount',
      headerName: 'expenses.table.amount',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'expenses.table.description',
      flex: 1.5,
    },
    {
      field: 'expenseDate',
      headerName: 'expenses.table.expenseDate',
      flex: 1.5,
      render: formatDateTR,
    },
    {
      field: 'status',
      headerName: 'expenses.table.status',
      flex: 1.6,
    },
    {
      field: 'approvingManager',
      headerName: 'expenses.table.approvingManager',
      flex: 1.5,
      render: value => {
        if (value && value.first_name && value.last_name) {
          return `${value.first_name} ${value.last_name}`;
        }
        return '-';
      },
    },
    {
      field: 'rejectReason',
      headerName: 'expenses.table.rejectReason',
      flex: 1.5,
    },
    {
      field: 'paidAmount',
      headerName: 'expenses.table.paidAmount',
      flex: 1.5,
      render: (value) => value ? `₺${parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-',
    },
    {
      field: 'approvingDate',
      headerName: 'expenses.table.approvingDate',
      flex: 1.5,
      render: formatDateTime,
    },
  ],
  leavesRequest: [
    {
      field: 'user',
      headerName: 'leaves.table.user',
      flex: 1.5,
      render: (value, row) => {
        return row.user ? `${row.user.first_name} ${row.user.last_name}` : '-';
      },
    },
    {
      field: 'remainingAnnualLeave',
      headerName: 'leaves.table.remainingAnnual',
      flex: 1.25,
      render: (value, row) => {
        const total =
          (row.user?.user_annual_leave_balance ?? 0) -
          (row.user?.total_leaves_used ?? 0);
        const isNegative = total < 0;
        const absHours = Math.abs(total);
        const days = Math.round(absHours / 8);
        const sign = isNegative ? '-' : '';
        return `${sign}${days} gün`;
      },
    },
    {
      field: 'startDate',
      headerName: 'leaves.table.startDate',
      flex: 1.25,
      sortable: true,
      render: formatDateTime,
    },
    {
      field: 'endDate',
      headerName: 'leaves.table.endDate',
      flex: 1.25,
      sortable: true,
      render: formatDateTime,
    },
    { field: 'leaveTypeName', headerName: 'leaves.table.leaveType', flex: 1 },
    { field: 'description', headerName: 'leaves.table.description', flex: 1.25 },
    { field: 'duration', headerName: 'leaves.table.duration', flex: 1 },
    {
      field: 'status',
      headerName: 'leaves.table.status',
      flex: 1.25,
    },
    {
      field: 'approvingManager',
      headerName: 'leaves.table.approvingManager',
      flex: 1.25,
      render: value => {
        if (value && value.first_name && value.last_name) {
          return `${value.first_name} ${value.last_name}`;
        }
        return '-';
      },
    },
  ],

  collections: [
    {
      field: 'luca_code',
      headerName: 'tahsilat.lucaCode',
      flex: 0.8,
      render: (value) => (
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: 'text.secondary' }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'firm_name',
      headerName: 'tahsilat.firmName',
      flex: 2,
      render: (value, row) => {
        const color = stringToColor(value);
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Avatar sx={{ width: 26, height: 26, bgcolor: color, fontSize: 10, fontWeight: 700, borderRadius: 1.5, flexShrink: 0 }}>
              {(row.firm_short_name || value || '').substring(0, 2).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12, lineHeight: 1.2 }}>{value}</Typography>
              {row.firm_city && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{row.firm_city}</Typography>
              )}
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'tl_bor',
      headerName: 'tahsilat.tlBor',
      flex: 1.2,
      sortable: true,
      render: (value) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'error.main' }}>
          {formatCurrency(value)}
        </Typography>
      ),
    },
    {
      field: 'tl_alacak',
      headerName: 'tahsilat.tlAlacak',
      flex: 1.2,
      sortable: true,
      render: (value) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'success.dark' }}>
          {formatCurrency(value)}
        </Typography>
      ),
    },
    {
      field: 'tl_bakiye',
      headerName: 'tahsilat.tlBakiye',
      flex: 1.2,
      sortable: true,
      render: (value) => {
        const n = Number(value);
        const color = n > 0 ? '#d32f2f' : n < 0 ? '#2e7d32' : '#616161';
        return (
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 800, color }}>
            {formatCurrency(value)}
          </Typography>
        );
      },
    },
    {
      field: 'import_date',
      headerName: 'tahsilat.importDate',
      flex: 1,
      render: formatDateTR,
    },
  ],

  reminders: [
    {
      field: 'firm_name',
      headerName: 'tahsilat.firmName',
      flex: 2,
      render: (value, row) => (
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12, lineHeight: 1.2 }}>{value}</Typography>
          {row.firm_short_name && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{row.firm_short_name}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'reminder_date',
      headerName: 'tahsilat.reminderDate',
      flex: 1,
      sortable: true,
      render: (value) => {
        if (!value) return '—';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        const isToday = d.getTime() === today.getTime();
        const isDue = d <= today;
        return (
          <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: isToday ? 'error.main' : isDue ? 'warning.dark' : 'text.primary' }}>
            {formatDateTR(value)}
          </Typography>
        );
      },
    },
    {
      field: 'note',
      headerName: 'tahsilat.note',
      flex: 2.5,
      render: (value) => value
        ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{value}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'status',
      headerName: 'tahsilat.status',
      flex: 1,
      render: (value) => {
        const colors = { today: 'error', due: 'warning', pending: 'info' };
        const labels = { today: 'Bugün', due: 'Tarihi geçti', pending: 'Bekliyor' };
        return (
          <Chip
            onClick={() => {}}
            label={labels[value] || 'Bekliyor'}
            size="small"
            color={colors[value] || 'info'}
            sx={{ height: 18, fontSize: 10, fontWeight: 700, border: 'none' }}
          />
        );
      },
    },
  ],

  paymentPromises: [
    {
      field: 'firm_name',
      headerName: 'tahsilat.firmName',
      flex: 2,
      render: (value, row) => (
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12, lineHeight: 1.2 }}>{value}</Typography>
          {row.firm_short_name && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{row.firm_short_name}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'promise_date',
      headerName: 'tahsilat.promiseDate',
      flex: 1,
      sortable: true,
      render: (value) => (
        <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {value ? formatDateTR(value) : '—'}
        </Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'tahsilat.amount',
      flex: 1.2,
      render: (value) => value != null
        ? <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'success.dark' }}>{formatCurrency(value)}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'promised_by',
      headerName: 'tahsilat.promisedBy',
      flex: 1.2,
      render: (value) => value || <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'note',
      headerName: 'tahsilat.note',
      flex: 2,
      render: (value) => value
        ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{value}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
  ],

  reportManagement: [
    {
      field: 'firmName',
      headerName: 'reportManagement.table.firm',
      flex: 1.5,
      render: (value, row, _rowIndex, t) => {
        const showProjectNumber = row.jobType === 'TUBITAK' && row.projectNumber;
        return (
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>
              {value || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
              {row.jobTitle || '-'}
            </Typography>
            {showProjectNumber && (
              <Typography sx={{ fontSize: '0.7rem', color: '#d97706', fontWeight: 600, mt: 0.25 }}>
                {(t ? t('reportManagement.table.projectNumber', { defaultValue: 'Proje No' }) : 'Proje No')}: {row.projectNumber}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'jobTypeLabel',
      headerName: 'reportManagement.table.jobType',
      flex: 1.2,
      render: (_value, row) => {
        if (!row.jobType) return '-';
        const JOB_TYPE_COLORS = {
          AR_GE: '#7c3aed', TASARIM_MERKEZI: '#4338ca', TEKNOPARK: '#0d9488',
          TUBITAK: '#d97706', ULUSLARARASI: '#0284c7',
        };
        const bg = JOB_TYPE_COLORS[row.jobType] || '#6366f1';
        return (
          <Chip onClick={() => {}} label={row.jobTypeLabel} size="small"
            sx={{ bgcolor: bg, color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 22, '& .MuiChip-label': { px: 0.75 } }}
          />
        );
      },
    },
    {
      field: 'reportTypeName',
      headerName: 'reportManagement.table.reportType',
      flex: 1.2,
      render: (value) => value
        ? <Chip onClick={() => {}} label={value} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
        : '-',
    },
    {
      field: 'technicalUserName',
      headerName: 'reportManagement.table.technicalResponsible',
      flex: 1,
      render: (value) => (
        <Typography sx={{ fontSize: '0.78rem', color: value ? 'text.primary' : 'text.disabled' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      field: 'financialUserName',
      headerName: 'reportManagement.table.financialResponsible',
      flex: 1,
      render: (value) => (
        <Typography sx={{ fontSize: '0.78rem', color: value ? 'text.primary' : 'text.disabled' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      field: 'inFlow',
      headerName: 'reportManagement.table.inFlow',
      flex: 0.8,
      render: (_value, row, _rowIndex, t) => (
        <Typography sx={{ fontSize: '0.78rem' }}>
          {row.inFlow
            ? (t ? t('common.yes', { defaultValue: 'Evet' }) : 'Evet')
            : (t ? t('common.no', { defaultValue: 'Hayır' }) : 'Hayır')}
        </Typography>
      ),
    },
    {
      field: 'reportTags',
      headerName: 'reportManagement.table.tags',
      flex: 1.5,
      render: (value) => {
        if (!Array.isArray(value) || value.length === 0) return '-';
        return (
          <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
            {value.slice(0, 2).map((tag) => (
              <Chip onClick={() => {}} key={tag} label={tag} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9' }} />
            ))}
            {value.length > 2 && (
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 600, lineHeight: '20px' }}>
                +{value.length - 2}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'startDate',
      headerName: 'reportManagement.table.startDate',
      flex: 1,
      render: (value) => value ? new Date(value).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'endDate',
      headerName: 'reportManagement.table.endDate',
      flex: 1,
      render: (value) => value ? new Date(value).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'statusLabel',
      headerName: 'reportManagement.table.status',
      flex: 1,
      render: (_value, row) => {
        const STATUS_COLORS = { PENDING: 'warning', IN_PROGRESS: 'info', COMPLETED: 'success', OVERDUE: 'error' };
        return (
          <Chip onClick={() => {}} label={row.statusLabel} color={STATUS_COLORS[row.status] || 'default'}
            size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
          />
        );
      },
    },
  ],

  // ─── Prim Yönetimi (Bonus Management) ──────────────────────────────────
  bonusRevenueTypes: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'code', headerName: 'bonusRevenueTypes.table.code', flex: 1,
      render: (v) => <Chip onClick={() => {}} label={v} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700 }} /> },
    { field: 'name', headerName: 'bonusRevenueTypes.table.name', flex: 2 },
    { field: 'applicable_channels_text', headerName: 'bonusRevenueTypes.table.applicableChannels', flex: 1.6 },
    { field: 'coefficient', headerName: 'bonusRevenueTypes.table.coefficient', flex: 1,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{Number(v).toFixed(4)}</span> },
  ],

  bonusRevenueRules: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.5, showIfRoot: true },
    { field: 'ciro_kanali', headerName: 'bonusRevenueRules.table.kanal', flex: 1.5,
      render: (v) => {
        const LBL = { SATIS_CIROSU: 'Satış Cirosu', ONAY_SONRASI_CIROSU: 'Onay Sonrası', PROJE_YAZIM_CIROSU: 'Proje Yazım' };
        return <Chip onClick={() => {}} label={LBL[v] || v} size="small" color="info" sx={{ height: 22, fontWeight: 600 }} />;
      } },
    { field: 'operation', headerName: 'bonusRevenueRules.table.operation', flex: 2,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{v}</span> },
    { field: 'team_name', headerName: 'bonusRevenueRules.table.team', flex: 1.5 },
    { field: 'aciklama', headerName: 'bonusRevenueRules.table.description', flex: 2 },
  ],

  bonusRules: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.2, showIfRoot: true },
    { field: 'team_name', headerName: 'bonusRules.table.team', flex: 1.2 },
    { field: 'period_label', headerName: 'bonusRules.table.period', flex: 1.4,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{v}</span> },
    { field: 'baz_prim_orani', headerName: 'bonusRules.table.bazOrani', flex: 1,
      render: (v, row) => row.sabit_yuzdelik_aktif
        ? <Chip onClick={() => {}} label={`Sabit %${row.sabit_yuzdelik_deger}`} size="small" color="warning" sx={{ height: 22 }} />
        : <span style={{ fontFamily: 'monospace' }}>{`%${v}`}</span> },
    { field: 'prim_hakedis_esik_orani', headerName: 'bonusRules.table.hakedisEsik', flex: 1,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{`%${v}`}</span> },
    { field: 'status', headerName: 'bonusRules.table.status', flex: 0.8,
      render: (v) => <Chip onClick={() => {}} label={v === 'active' ? 'Aktif' : 'Pasif'}
        size="small" color={v === 'active' ? 'success' : 'default'} sx={{ height: 22 }} /> },
  ],

  bonusPayments: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.2, showIfRoot: true },
    { field: 'tarih', headerName: 'bonusPayments.table.tarih', flex: 1,
      render: (v) => v ? new Date(v).toLocaleDateString('tr-TR') : '-' },
    { field: 'user_name', headerName: 'bonusPayments.table.user', flex: 1.5 },
    { field: 'islem_turu', headerName: 'bonusPayments.table.islemTuru', flex: 1.2,
      render: (v, row) => {
        const COLOR = { HESAPLANAN_PRIM: 'info', EK_PRIM: 'success', ODEME: 'warning' };
        return <Chip onClick={() => {}} label={row.islem_turu_label} size="small" color={COLOR[v] || 'default'} sx={{ height: 22, fontWeight: 600 }} />;
      } },
    { field: 'period_label', headerName: 'bonusPayments.table.period', flex: 1,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
    { field: 'tutar', headerName: 'bonusPayments.table.tutar', flex: 1,
      render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span> },
    { field: 'aciklama', headerName: 'bonusPayments.table.description', flex: 2 },
  ],

  userBonusCoefficients: [
    { field: 'user_name', headerName: 'userBonusCoefficients.table.user', flex: 1.5 },
    { field: 'yil', headerName: 'userBonusCoefficients.table.yil', flex: 0.7 },
    { field: 'ceyrek', headerName: 'userBonusCoefficients.table.ceyrek', flex: 0.7,
      render: (v) => `Q${v}` },
    { field: 'katsayi', headerName: 'userBonusCoefficients.table.katsayi', flex: 1,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{Number(v).toFixed(3)}</span> },
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.2, showIfRoot: true },
  ],

  bonusTargets: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.2, showIfRoot: true },
    { field: 'target_kapsami', headerName: 'bonusTargets.table.scope', flex: 1.5,
      render: (v) => <Chip onClick={() => {}} label={v} size="small" sx={{ height: 22, fontWeight: 600 }} /> },
    { field: 'period_label', headerName: 'bonusTargets.table.period', flex: 0.8,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
    { field: 'hedef_tutar', headerName: 'bonusTargets.table.hedefTutar', flex: 1.2,
      render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span> },
    { field: 'aciklama', headerName: 'bonusTargets.table.aciklama', flex: 2 },
  ],

  userRevenueEntries: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.2, showIfRoot: true },
    { field: 'period_label', headerName: 'userRevenueEntries.table.period', flex: 0.8,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
    { field: 'ciro_kanali_label', headerName: 'userRevenueEntries.table.kanal', flex: 1.3,
      render: (v, row) => {
        const COLOR = {
          SATIS_CIROSU: 'primary',
          PROJE_YAZIM_CIROSU: 'info',
          ONAY_SONRASI_CIROSU: 'success',
        };
        return <Chip onClick={() => {}} label={v} size="small"
          color={COLOR[row.ciro_kanali] || 'default'} sx={{ height: 22, fontWeight: 600 }} />;
      } },
    { field: 'firm_name', headerName: 'userRevenueEntries.table.firm', flex: 1.3,
      render: (v, row, _idx, t) => {
        const pn = row?.job?.project_number;
        if (!pn) return v;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 500 }}>{v}</Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {t('userRevenueEntries.table.projectNumber')}: {pn}
            </Typography>
          </Box>
        );
      } },
    { field: 'project_number', headerName: 'userRevenueEntries.table.projectNumber', flex: 1,
      render: (v) => v || <span style={{ color: '#9aa0a6' }}>—</span> },
    // GENISLIK — Ibrahim (kayit 22, 28:23): "Onu genislettirim biraz
    // daha... bu satiri, prim odeme kismini soyle buyuklukte yaparim."
    //
    // OLCU: alt satir "toplam 13/13" = 12 karakter, monospace 11px
    // (~6.6px/karakter) = 79px, arti hucre dolgusu 2x16px → 111px.
    // Sutun genisligi flex'in diger 12 kolona (toplam flex 13.6) oranina
    // gore cikiyor ve tablonun en dar hali 800px:
    //   flex 0.8 →  47px   kesiliyordu
    //   flex 1.3 →  70px   hala kesiliyordu ("toplam 13/1")
    //   flex 2.6 → 128px   tam sigiyor, 17px pay kaliyor
    //
    // Sadece flex: bu tablonun genislik mekanizmasi bu. Onceki denemede
    // ayrica `minWidth` verip MyTable'i de ona gore degistirmistim;
    // paylasilan bilesene tek bir sutun icin dokunmak yanlisti ve
    // tabloyu bozdu, geri alindi.
    { field: 'xyLabel', headerName: 'userRevenueEntries.table.xy', flex: 1.5,
      render: (_v, row) => {
        if (row?.ciro_kanali !== 'ONAY_SONRASI_CIROSU') {
          return <span style={{ color: '#9aa0a6' }}>—</span>;
        }
        const label = row?.xyLabel;
        if (!label) return <span style={{ color: '#9aa0a6' }}>—</span>;
        // Satirin kendi suresi ustte; ayni is+kanal icin o ana kadar
        // birikmis toplam altinda kucuk puntoyla. Birikim satirin kendi
        // degerine esitse (ilk kayit) alt satir hic basilmaz.
        //
        // TOOLTIP — Ibrahim (kayit 22, 28:34): "Toplamada tooltip koy."
        // Dar ekranda satir kaydiginda deger ustune gelince okunsun.
        return (
          <Tooltip arrow title={`top: ${row?.cumulativeLabel || label}`}>
            <Box sx={{ lineHeight: 1.25 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{label}</span>
              {row?.cumulativeLabel ? (
                <Typography
                  component="div"
                  sx={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280', mt: 0.25 }}
                >
                  {`toplam ${row.cumulativeLabel}`}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      } },
    { field: 'job_type_label', headerName: 'userRevenueEntries.table.jobType', flex: 1.1,
      render: (v) => v || <span style={{ color: '#9aa0a6' }}>—</span> },
    { field: 'bonusRevenueTypeName', headerName: 'userRevenueEntries.table.bonusRevenueType', flex: 1.4,
      render: (v) => v || <span style={{ color: '#9aa0a6' }}>—</span> },
    { field: 'tutar', headerName: 'userRevenueEntries.table.tutar', flex: 1,
      render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatTL(v)}</span>,
      // CSV/Excel icin ham sayi (TL isareti yok) — kullanici formul yazip
      // uzerinde islem yapabilsin. TR ondalik icin virgul.
      exportValue: (_row, v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : '';
      } },
    { field: 'targetRevenue', headerName: 'userRevenueEntries.table.targetRevenue', flex: 1.1,
      render: (_v, row) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#3a5af0' }}>
          {formatTL(row.targetRevenue)}
        </span>
      ),
      exportValue: (row) => {
        const n = Number(row?.targetRevenue);
        return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : '';
      } },
    { field: 'personName', headerName: 'userRevenueEntries.table.person', flex: 1.1,
      render: (v) => v || <span style={{ color: '#9aa0a6' }}>—</span> },
    { field: 'status', headerName: 'userRevenueEntries.table.status', flex: 0.9,
      render: (v) => <RevenueStatusChip value={v} /> },
    { field: 'warningsKeys', headerName: 'userRevenueEntries.table.warnings', flex: 1.4,
      render: (_v, row) => <RevenueWarningsCell keys={row.warningsKeys} /> },
  ],

  // ─── Ciro Aktar (lider tarafindan ceyrekler arasi tasima) ───────────────
  // enriched rows beklenir: personName, firmName, jobLabel, channelLabel,
  // yil, ceyrek, tutar, targetRevenue ve transfer_logs (audit trail).
  ciroAktar: [
    { field: 'firmName', headerName: 'ciroAktar.columns.firm', flex: 1 },
    { field: 'personName', headerName: 'ciroAktar.columns.person', flex: 1 },
    { field: 'jobLabel', headerName: 'ciroAktar.columns.job', flex: 1 },
    { field: 'channelLabel', headerName: 'ciroAktar.columns.channel', flex: 1 },
    { field: 'yil', headerName: 'ciroAktar.columns.year', flex: 0.7 },
    { field: 'ceyrek', headerName: 'ciroAktar.columns.quarter', flex: 0.5,
      render: (value) => `Q${value}` },
    { field: 'tutar', headerName: 'ciroAktar.columns.amount', flex: 0.9,
      render: (_v, row) => (
        <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{formatTL(row.tutar)}</span>
      ) },
    { field: 'targetRevenue', headerName: 'ciroAktar.columns.targetAmount', flex: 1,
      render: (_v, row) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatTL(row.targetRevenue)}
        </span>
      ) },
    // Audit trail kolonu — tiklanir hucre. Hucreye basinca tum gecmis
    // aktarimlar Popover icinde aciliyor (kim, ne zaman, Q?->Q?, not).
    { field: 'transferHistory', headerName: 'ciroAktar.columns.transferHistory', flex: 1.5,
      render: (_v, row) => <TransferHistoryCell logs={row.transfer_logs} /> },
  ],

  revenueCalculationRules: [
    { field: 'company_name', headerName: 'table.columns.company', flex: 1.2, showIfRoot: true },
    { field: 'jobTypeName', headerName: 'revenueCalculationRules.table.jobType', flex: 1.3 },
    { field: 'revenueChannelText', headerName: 'revenueCalculationRules.table.revenueChannel', flex: 1.3,
      render: (v) => <Chip onClick={() => {}} label={v} size="small" color="info" sx={{ height: 22, fontWeight: 600 }} /> },
    { field: 'pricingMethodNames', headerName: 'revenueCalculationRules.table.pricingMethod', flex: 1.8,
      render: (names) => {
        const list = Array.isArray(names) ? names : [];
        if (list.length === 0) return <span style={{ color: '#9aa0a6' }}>—</span>;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {list.slice(0, 3).map((name, i) => (
              <Chip key={`${name}-${i}`} onClick={() => {}} label={name} size="small" sx={{ height: 20, fontSize: 11 }} />
            ))}
            {list.length > 3 && <Chip onClick={() => {}} label={`+${list.length - 3}`} size="small" sx={{ height: 20, fontSize: 11 }} />}
          </Box>
        );
      } },
    { field: 'recurrenceTypeText', headerName: 'revenueCalculationRules.table.recurrence', flex: 1.2,
      render: (v, row) => (
        <span>
          {v}
          {row.recurrence_type === 'DEFINED_MONTHS' && row.defined_months
            ? ` (${row.defined_months} ay)`
            : ''}
        </span>
      ) },
    { field: 'validJobStatuses', headerName: 'revenueCalculationRules.table.validJobStatuses', flex: 1.8,
      render: (arr) => {
        const list = Array.isArray(arr) ? arr : [];
        if (list.length === 0) return <span style={{ color: '#9aa0a6' }}>—</span>;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {list.slice(0, 3).map((s) => (
              <Chip key={s} onClick={() => {}} label={s} size="small" sx={{ height: 20, fontSize: 11 }} />
            ))}
            {list.length > 3 && <Chip onClick={() => {}} label={`+${list.length - 3}`} size="small" sx={{ height: 20, fontSize: 11 }} />}
          </Box>
        );
      } },
    { field: 'feeSourceText', headerName: 'revenueCalculationRules.table.feeSource', flex: 1.5 },
    { field: 'status', headerName: 'revenueCalculationRules.table.status', flex: 0.8,
      render: (v) => <Chip onClick={() => {}}
        label={v === 'active' ? 'Aktif' : 'Pasif'}
        size="small"
        color={v === 'active' ? 'success' : 'default'}
        sx={{ height: 22 }} /> },
    { field: 'created_at', headerName: 'table.columns.createdAt', flex: 1, render: formatDateTR },
  ],

  // ─── Doküman Bankası ────────────────────────────────────────────────────
  // Kolonlar: dosya tipi ikonu + başlık, açıklama, versiyon chip'i,
  // yükleyen kişi, güncelleme tarihi ve atama sayısı.
  documentBank: [
    {
      field: 'file_type',
      headerName: 'documentBank.columns.fileType',
      flex: 0.6,
      sortable: false,
      render: (_value, row) => {
        const latest = row.versions?.[0];
        const meta = getDocumentFileTypeMeta(latest?.mime_type || latest?.file_name);
        const { Icon, color, label } = meta;
        return (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            px: 1, py: 0.4, borderRadius: 1.25,
            bgcolor: `${color}15`,
            border: `1px solid ${color}33`,
          }}>
            <Icon sx={{ fontSize: 18, color }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 0.3 }}>
              {label}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'title',
      headerName: 'documentBank.columns.title',
      flex: 1.6,
      sortable: true,
      render: (value) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      field: 'uploaded_by',
      headerName: 'documentBank.columns.uploadedBy',
      flex: 1.25,
      sortable: false,
      render: (_value, row) => {
        const uploader = row.versions?.[0]?.uploader || row.creator;
        if (!uploader) return '-';
        const name = `${uploader.first_name || ''} ${uploader.last_name || ''}`.trim() || '-';
        const initials = `${(uploader.first_name || '?')[0]}${(uploader.last_name || '')[0] || ''}`.toUpperCase();
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: 'primary.light' }}>
              {initials}
            </Avatar>
            <Typography sx={{ fontSize: 12.5 }}>{name}</Typography>
          </Stack>
        );
      },
    },
    {
      field: 'assignments_count',
      headerName: 'documentBank.columns.assignmentsCount',
      flex: 0.75,
      sortable: false,
      render: (_value, row, _rowIndex, t) => {
        const count = row.assignments?.length || 0;
        if (count === 0) {
          return (
            <Chip
              onClick={() => {}}
              size="small"
              label={t ? t('documentBank.stats.noAssignment', 'Atama yok') : '-'}
              variant="outlined"
              sx={{ height: 22, fontSize: 11 }}
            />
          );
        }
        return (
          <Chip
            onClick={() => {}}
            size="small"
            color="info"
            label={t
              ? t('documentBank.stats.assignmentsCount', '{{count}} atama', { count })
              : `${count}`}
            sx={{ height: 22, fontWeight: 600, fontSize: 11.5 }}
          />
        );
      },
    },
    {
      field: 'category',
      headerName: 'documentBank.columns.category',
      flex: 0.85,
      sortable: true,
      render: (value, _row, _idx, t) => {
        if (!value) return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>-</Typography>;
        const map = {
          TEKNIK: { color: 'primary', label: t ? t('documentBank.category.TEKNIK', 'Teknik') : 'Teknik' },
          MALI: { color: 'success', label: t ? t('documentBank.category.MALI', 'Mali') : 'Mali' },
          SATIS: { color: 'warning', label: t ? t('documentBank.category.SATIS', 'Satış') : 'Satış' },
        };
        const cfg = map[value] || { color: 'default', label: value };
        return <Chip onClick={() => {}} size="small" color={cfg.color} variant="outlined"
          label={cfg.label} sx={{ height: 22, fontWeight: 600, fontSize: 11 }} />;
      },
    },
    {
      field: 'process',
      headerName: 'documentBank.columns.process',
      flex: 0.9,
      sortable: true,
      render: (value, _row, _idx, t) => {
        if (!value) return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>-</Typography>;
        const map = {
          ONAY_SONRASI: t ? t('documentBank.process.ONAY_SONRASI', 'Onay sonrası') : 'Onay sonrası',
          YAZIM: t ? t('documentBank.process.YAZIM', 'Yazım') : 'Yazım',
          DIGER: t ? t('documentBank.process.DIGER', 'Diğer') : 'Diğer',
        };
        return <Chip onClick={() => {}} size="small" variant="outlined"
          label={map[value] || value} sx={{ height: 22, fontSize: 11 }} />;
      },
    },
    {
      // row.tags — enrichment `[{id, name, color}]` olarak duzlestirir
      // (jobs.tags pattern). DataTableContainer etiket filter'i bu yapiyi
      // beklyor.
      field: 'tags',
      headerName: 'documentBank.columns.tags',
      flex: 1.2,
      sortable: false,
      render: (_value, row) => {
        const list = row.tags || [];
        if (list.length === 0) {
          return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>-</Typography>;
        }
        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {list.slice(0, 3).map(tg => (
              <Chip key={tg.id} size="small" label={tg.name || '?'}
                onClick={() => {}} 
              sx={{
                  height: 20, fontSize: 10.5, fontWeight: 600,
                  bgcolor: `${tg.color || '#3b82f6'}20`,
                  color: tg.color || '#3b82f6',
                  border: `1px solid ${tg.color || '#3b82f6'}55`,
                }} />
            ))}
            {list.length > 3 && (
              <Chip size="small" label={`+${list.length - 3}`}
                sx={{ height: 20, fontSize: 10.5 }} />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'documentBank.columns.status',
      flex: 0.7,
      sortable: true,
      render: (value, _row, _idx, t) => (
        <Chip onClick={() => {}} size="small"
          color={value === false ? 'default' : 'success'}
          variant={value === false ? 'outlined' : 'filled'}
          label={value === false
            ? (t ? t('documentBank.status.passive', 'Pasif') : 'Pasif')
            : (t ? t('documentBank.status.active', 'Aktif') : 'Aktif')}
          sx={{ height: 22, fontWeight: 600, fontSize: 11 }} />
      ),
    },
  ],
};

export const FILTER_CONFIGS = {
  users: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'companyName',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
      hasuraField: 'company.name',
    },
    {
      field: 'role',
      label: 'table.filters.role',
      filterType: 'exact',
      i18nPath: 'table.values.roles',
    },
    {
      field: 'titleName',
      label: 'table.filters.title',
      filterType: 'exact',
      hasuraField: 'title.title_name',
    },
    {
      field: 'teamName',
      label: 'table.filters.team',
      filterType: 'contains',
      hasuraField: 'team.name',
    },
  ],

  groups: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'companyName',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
      hasuraField: 'company.name',
    },
  ],

  companies: [],

  teams: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'companyName',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
      hasuraField: 'company.name',
    },
    {
      field: 'groupName',
      label: 'table.filters.group',
      filterType: 'contains',
      hasuraField: 'group.name',
    },
  ],

  teamMembers: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      key: 'companyId',
      type: 'select',
      label: 'table.filters.company',
      options: [],
      showIfRoot: true,
    },
    {
      field: 'status',
      label: 'table.filters.status',
      filterType: 'exact',
      i18nPath: 'table.values.memberStatus',
    },
  ],

  teamFirms: [],

  userFirms: [],

  titles: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'status',
      label: 'table.filters.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'table.values.status.active' },
        { value: 'inactive', labelKey: 'table.values.status.inactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  leaveTypes: [
    {
      field: 'status',
      label: 'leaveTypes.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'leaveTypes.table.statusActive' },
        { value: 'inactive', labelKey: 'leaveTypes.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  partners: [
    {
      field: 'status',
      label: 'partners.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'partners.table.statusActive' },
        { value: 'inactive', labelKey: 'partners.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  sectors: [
    {
      field: 'status',
      label: 'sectors.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'sectors.table.statusActive' },
        { value: 'inactive', labelKey: 'sectors.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  contractSubjects: [
    {
      field: 'status',
      label: 'contractSubjects.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'contractSubjects.table.statusActive' },
        {
          value: 'inactive',
          labelKey: 'contractSubjects.table.statusInactive',
        },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  jobTypes: [
    {
      field: 'status',
      label: 'jobTypes.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'jobTypes.table.statusActive' },
        { value: 'inactive', labelKey: 'jobTypes.table.statusInactive' },
      ],
    },
    {
      field: 'statusGroup',
      label: 'jobTypes.table.statusGroup',
      filterType: 'exact',
      options: [
        { value: 'standard', labelKey: 'jobTypes.statusGroups.standard' },
        { value: 'phased', labelKey: 'jobTypes.statusGroups.phased' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
    },
  ],

  scopeOfWork: [
    {
      field: 'status',
      label: 'scopeOfWork.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'scopeOfWork.table.statusActive' },
        { value: 'inactive', labelKey: 'scopeOfWork.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  pricingMethods: [
    {
      field: 'status',
      label: 'pricingMethods.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'pricingMethods.table.statusActive' },
        { value: 'inactive', labelKey: 'pricingMethods.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  workStatus: [
    {
      field: 'status',
      label: 'workStatus.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'workStatus.table.statusActive' },
        { value: 'inactive', labelKey: 'workStatus.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  reportTypes: [
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
    {
      field: 'status',
      label: 'reportTypes.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'reportTypes.table.statusActive' },
        { value: 'inactive', labelKey: 'reportTypes.table.statusInactive' },
      ],
    }
  ],

  tasks: [
    {
      field: 'firmName',
      label: 'tasks.table.firm',
      filterType: 'contains',
      hasuraField: 'firm.firm_name',
    },
    {
      field: 'companyName',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
      hasuraField: 'company.name',
    },
    {
      field: 'taskStatus',
      label: 'tasks.table.status',
      filterType: 'exact',
      options: [
        { value: 'PENDING', label: 'Pending' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'CANCELLED', label: 'Cancelled' },
      ],
      hasuraField: 'task_status',
    },
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
  ],

  tags: [
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
    {
      field: 'tags_page',
      label: 'tags.table.page',
      filterType: 'exact',
      options: [
        { value: 'JOBS', label: 'tags.pages.jobs' },
      ],
    }
  ],

  documentBankTags: [
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
    },
  ],

  firms: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'isEstablishedText',
      label: 'firms.table.isEstablished',
      filterType: 'exact',
      options: [
        { value: 'Evet', label: 'Evet' },
        { value: 'Hayır', label: 'Hayır' },
      ],
      // Display 'Evet'/'Hayır' maps to a boolean column — keep client-only.
      noServerFilter: true,
    },
    {
      field: 'partnerReferralText',
      label: 'firms.table.partnerReferral',
      filterType: 'exact',
      options: [
        { value: 'Evet', label: 'Evet' },
        { value: 'Hayır', label: 'Hayır' },
      ],
      noServerFilter: true,
    },
    {
      field: 'firm_city',
      label: 'firms.table.city',
      filterType: 'contains',
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
      hasuraField: 'company.name',
    },
  ],

  firm_cards: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'firm_cart_title',
      label: 'firms.firmCards.table.title',
      filterType: 'contains',
    },
    {
      field: 'statusText',
      label: 'firms.firmCards.table.status',
      filterType: 'exact',
      options: [
        { value: 'Aktif', label: 'Aktif' },
        { value: 'Pasif', label: 'Pasif' },
      ],
    },
  ],

  leaves: [

    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Sadece ROOT için
      hasuraField: 'user.company.name',
    },
    {
      field: 'group_name',
      label: 'table.filters.group',
      filterType: 'contains',
      showIfRoot: true, // ROOT için
      showIfDirector: true, // DIRECTOR için
      hasuraField: 'user.team.group.name',
    },
    {
      field: 'team_name',
      label: 'table.filters.team',
      filterType: 'contains',
      showIfRoot: true, // ROOT için
      showIfDirector: true, // DIRECTOR için
      showIfGroupManager: true, // GROUP_MANAGER için
      hasuraField: 'user.team.name',
    },
    {
      field: 'leaveTypeName',
      label: 'leaves.table.leaveType',
      filterType: 'exact',
      hasuraField: 'leaveType.leave_type_name',
    },
  ],

  expenses: [
    {
      field: 'company_name',
      label: 'table.filters.company',
      showIfRoot: true, // Sadece ROOT için
      filterType: 'contains',
      hasuraField: 'user.company.name',
    },
    {
      field: 'group_name',
      label: 'table.filters.group',
      filterType: 'contains',
      hasuraField: 'user.team.group.name',
    },
    {
      field: 'team_name',
      label: 'table.filters.team',
      filterType: 'contains',
      hasuraField: 'user.team.name',
    },
  ],
  expenseTypes: [
    {
      field: 'status',
      label: 'expenseTypes.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'expenseTypes.table.statusActive' },
        { value: 'inactive', labelKey: 'expenseTypes.table.statusInactive' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  invoiceCodes: [
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
    },
  ],

  contracts: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
      hasuraField: 'company.name',
    },
    {
      field: 'firm_name',
      label: 'contracts.table.firm',
      filterType: 'contains',
      hasuraField: 'firm.firm_name',
    },
    {
      field: 'firm_short_name',
      label: 'contracts.table.firmShortName',
      filterType: 'contains',
      hasuraField: 'firm.firm_short_name',
    },
    {
      field: 'work_started_without_contract',
      label: 'contracts.table.contractReceived',
      filterType: 'exact',
      // Transform'ta boolean → 'Geldi'/'Gelmedi' (useContracts:62) — filter
      // değerleri aynı string'lerle eşleşmek zorunda.
      options: [
        { value: 'Geldi', label: 'Geldi' },
        { value: 'Gelmedi', label: 'Gelmedi' },
      ],
      noServerFilter: true,
    },
    {
      field: 'contract_subject_name',
      label: 'contracts.table.contractSubject',
      filterType: 'contains',
      hasuraField: 'contract_subject.name',
    },
    {
      field: 'scope_of_work_name',
      label: 'contracts.table.scopeOfWork',
      filterType: 'contains',
      hasuraField: 'scope_of_work.name',
    },
  ],

  proposal_status: [
    {
      field: 'status',
      label: 'proposalStatuses.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', label: 'Aktif' },
        {value: 'inactive', label: 'Pasif' },
      ],
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
    },
  ],

  offers: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'contract_subject_name',
      label: 'offers.table.contractSubject',
      filterType: 'contains',
      hasuraField: 'contract_subject.name',
    },
    {
      field: 'scope_of_work_name',
      label: 'offers.table.scopeOfWork',
      filterType: 'contains',
      hasuraField: 'scope_of_work.name',
    },
    {
      field: 'proposal_status_name',
      label: 'offers.table.proposalStatus',
      filterType: 'exact',
      options: [],
      dynamicOptions: 'proposalStatuses',
      hasuraField: 'proposal_status.name',
    },
    {
      field: 'created_by_name',
      label: 'offers.table.createdBy',
      filterType: 'contains',
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true, // Only show for ROOT users
      hasuraField: 'company.name',
    },
  ],

    jobs: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
      // Map the display-friendly column to the Hasura relation path so the
      // serverFilters mode (see utils/buildHasuraWhere.js) targets the actual
      // jobs.company.name column.
      hasuraField: 'company.name',
    },
    {
      field: 'firm_name',
      label: 'firms.table.firmName',
      filterType: 'contains',
      hasuraField: 'contract.firm.firm_name',
    },
    {
      field: 'firm_short_name',
      label: 'firms.table.firmShortName',
      filterType: 'contains',
      hasuraField: 'contract.firm.firm_short_name',
    },
    {
      field: 'job_type',
      label: 'jobs.table.jobType',
      // Coklu secim: birden fazla is turu ayni anda filtrelenebilir. Server
      // tarafi hasuraField `job_type_id` uzerinden `_in` operatoru ile calisir
      // (buildHasuraWhere multi-select array degerini `_in` cevirir).
      filterType: 'multiselect',
      // Options page'deki customFilterConfigs ile dinamik dolduruluyor.
      options: [],
    },
    {
      field: 'job_status',
      label: 'jobs.table.jobStatus',
      filterType: 'multiselect',
      // Options page'deki customFilterConfigs ile mevcut iş kayıtlarındaki
      // distinct status değerlerinden dolduruluyor (statik enum listesi değil).
      options: [],
    },
    {
      // Sözleşme gelme durumu. Display kolonu 'contract_received' türetilmiş
      // alan; server-filter Hasura'da contract.work_started_without_contract
      // boolean kolonuna gider. UI 'yes'/'no' string değeri kullanır (MUI
      // Select false değerini boş sayar), transformValue boolean'a çevirir.
      // Option labelKey'leri JobManagementPage.customFilterConfigs'te t() ile
      // çevrilir.
      field: 'contract_received',
      label: 'contracts.table.contractReceived',
      filterType: 'exact',
      hasuraField: 'contract.work_started_without_contract',
      transformValue: value => value === 'yes',
      options: [
        { value: 'yes', labelKey: 'contracts.form.options.came' },
        { value: 'no', labelKey: 'contracts.form.options.didNotCome' },
      ],
    },
    {
      field: 'tags',
      label: 'jobs.table.tags',
      filterType: 'multiselect',
      options: [],
    },
    {
      // Display column 'technical_responsible_name' (concatenated name) kalır;
      // filter `technical_user_id`'ye map'lendi. Options
      // JobManagementPage:customFilterConfigs'te sadece TEKNIK role sahip
      // kullanicilarla dolduruluyor. Coklu secim: birden fazla teknik sorumlu
      // ayni anda filtrelenebilir (Hasura `_in`).
      field: 'technical_user_id',
      label: 'jobAssignments.table.technicalUserName',
      filterType: 'multiselect',
      hasuraField: 'job_assignment.technical_user_id',
    },
    {
      field: 'financial_user_id',
      label: 'jobAssignments.table.financialUserName',
      filterType: 'multiselect',
      hasuraField: 'job_assignment.financial_user_id',
    },
  ],

  invoices: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'company_name',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
      hasuraField: 'company.name',
    },
    {
      field: 'firm_name',
      label: 'firms.table.firmName',
      filterType: 'contains',
      // Invoices have no direct firm relation — only via contract.
      hasuraField: 'contract.firm.firm_name',
    },
    {
      field: 'firm_short_name',
      label: 'firms.table.firmShortName',
      filterType: 'contains',
      hasuraField: 'contract.firm.firm_short_name',
    },
    {
      field: 'job_type',
      label: 'invoices.filters.jobType',
      filterType: 'multiselect',
      hasuraField: 'job.job_type',
      // Options are translated in InvoiceManagementPage.customFilterConfigs
      // (raw i18n keys go to `labelKey`; DynamicCrudPage only translates
      // `labelKey` for the `status` filter otherwise).
      options: [
        { value: 'TUBITAK', labelKey: 'jobs.jobTypes.TUBITAK' },
        { value: 'TEKNOPARK', labelKey: 'jobs.jobTypes.TEKNOPARK' },
        { value: 'AR_GE', labelKey: 'jobs.jobTypes.AR_GE' },
        { value: 'TASARIM_MERKEZI', labelKey: 'jobs.jobTypes.TASARIM_MERKEZI' },
        { value: 'ULUSLARARASI', labelKey: 'jobs.jobTypes.ULUSLARARASI' },
        { value: 'DIGER', labelKey: 'jobs.jobTypes.DIGER' },
      ],
    },
    {
      // Ucretlendirme sureci: server-side ID bazli filter. Options runtime
      // olarak InvoiceManagementPage.customFilterConfigs icinde additionalData.
      // pricingProcess.pricingProcess listesinden dolduruluyor (tum liste,
      // sayfa datasindan degil).
      field: 'pricing_process_id',
      label: 'invoices.table.pricingProcess',
      filterType: 'multiselect',
      hasuraField: 'pricing_process_id',
      options: [],
    },
  ],

  jobAssignments: [

    {
      field: 'jobStatus',
      label: 'jobs.table.jobStatus',
      filterType: 'contains',
      hasuraField: 'job.job_status',
    },
    {
      field: 'technicalUserName',
      label: 'jobAssignments.table.technicalUserName',
      filterType: 'contains',
      // Concatenated first_name + last_name from technical_user relation —
      // no single column maps cleanly. Skip in serverFilters mode.
      noServerFilter: true,
    },
    {
      field: 'technicalTeamName',
    label: 'jobAssignments.table.technicalTeamName',
    filterType: 'contains',
    hasuraField: 'technical_team.name',
    },
    {
      field: 'warningType',
      label: 'jobAssignments.table.warning',
      filterType: 'exact',
      options: [
        { value: 'TECH_LEFT', label: 'jobAssignments.warnings.technicalUserLeftShort' },
        { value: 'NO_FINANCIAL', label: 'jobAssignments.warnings.missingFinancialShort' },
        { value: 'TECH_LEFT_AND_NO_FINANCIAL', label: 'jobAssignments.warnings.bothShort' },
      ],
    },
  ],

  paymentPlanTemplates: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'companyName',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
    },
    {
      field: 'year',
      label: 'paymentPlanTemplates.table.year',
      filterType: 'exact',
    },
    {
      field: 'status',
      label: 'paymentPlanTemplates.table.status',
      filterType: 'exact',
      options: [
        { value: true, labelKey: 'paymentPlanTemplates.table.statusActive' },
        { value: false, labelKey: 'paymentPlanTemplates.table.statusInactive' },
      ],
    },
  ],

  pricingProcess: [
    {
      field: 'created_at',
      label: 'table.filters.createdAt',
      filterType: 'daterange',
    },
    {
      field: 'companyName',
      label: 'table.filters.company',
      filterType: 'contains',
      showIfRoot: true,
    },
    {
      field: 'status',
      label: 'pricingProcess.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'pricingProcess.table.statusActive' },
        { value: 'inactive', labelKey: 'pricingProcess.table.statusInactive' },
      ],
    },
  ],

  reportManagement: [
    {
      field: 'endDate',
      label: 'reportManagement.filters.dateRange',
      filterType: 'daterange',
    },
    // Coklu secim — jobs sayfasi ile ayni pattern: birden fazla is turu / rapor
    // turu / durum / sorumlu ayni anda filtrelenebilir. Server tarafi
    // buildHasuraWhere multi-select array degerini `_in`'e cevirir.
    {
      field: 'jobTypeLabel',
      label: 'reportManagement.filters.jobType',
      filterType: 'multiselect',
    },
    {
      field: 'reportTypeName',
      label: 'reportManagement.filters.reportType',
      filterType: 'multiselect',
      type: 'select',
    },
    {
      field: 'statusLabel',
      label: 'reportManagement.filters.status',
      filterType: 'multiselect',
    },
    {
      field: 'technicalUserName',
      label: 'reportManagement.filters.technicalResponsible',
      filterType: 'multiselect',
    },
    {
      field: 'financialUserName',
      label: 'reportManagement.filters.financialResponsible',
      filterType: 'multiselect',
    },
    {
      field: 'inFlowLabel',
      label: 'reportManagement.filters.inFlow',
      filterType: 'multiselect',
      // Options translated at runtime in ReportManagementPage (DataTable expects pre-translated labels).
    },
    {
      field: 'reportTags',
      label: 'reportManagement.filters.tags',
      filterType: 'multiselect',
    },
  ],

  // ─── Prim Yönetimi ──────────────────────────────────────────────────────
  // Pattern: contains tipinde alan-bazlı text filtreler + company_name
  // auto-derive (DynamicCrudPage), tarih için daterange. Enum'lar için
  // transformData label kolonları (örn. ciro_kanali_label) üzerinden filtre.
  bonusRevenueTypes: [
    { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
  ],

  bonusRevenueRules: [
    { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    { field: 'ciro_kanali_label', label: 'bonusRevenueRules.table.kanal', filterType: 'contains' },
    { field: 'team_name', label: 'bonusRevenueRules.table.team', filterType: 'contains' },
  ],

  bonusRules: [
        { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    { field: 'team_name', label: 'bonusRules.table.team', filterType: 'contains' },
        {
      field: 'status',
      label: 'bonusRules.table.status',
      filterType: 'exact',
      options: [
        { value: 'active',   label: 'Aktif' },
        { value: 'inactive', label: 'Pasif' },
      ],
    },
  ],

  bonusPayments: [
    { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    { field: 'tarih', label: 'bonusPayments.table.tarih', filterType: 'daterange' },
    { field: 'user_name', label: 'bonusPayments.table.user', filterType: 'contains' },
    { field: 'islem_turu_label', label: 'bonusPayments.table.islemTuru', filterType: 'contains' },
    { field: 'period_label', label: 'bonusPayments.table.period', filterType: 'contains' },
  ],

  userBonusCoefficients: [
    { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    { field: 'user_name', label: 'userBonusCoefficients.table.user', filterType: 'contains' },
  ],

  bonusTargets: [
    { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    { field: 'target_kapsami', label: 'bonusTargets.table.scope', filterType: 'contains' },
    { field: 'period_label', label: 'bonusTargets.table.period', filterType: 'contains' },
  ],

  userRevenueEntries: [
    { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    { field: 'period_label', label: 'userRevenueEntries.table.period', filterType: 'contains' },
    { field: 'ciro_kanali_label', label: 'userRevenueEntries.table.kanal', filterType: 'contains' },
    { field: 'firm_name', label: 'userRevenueEntries.table.firm', filterType: 'contains' },
    // Ciro Turu — bonusRevenueTypeName enrichment tarafinda flatten
    // ediliyor; contains filter ile diger dinamik filtrelerle ayni pattern.
    // Options DataTable tarafindan tablonun verilerinden otomatik uretilir.
    { field: 'bonusRevenueTypeName', label: 'userRevenueEntries.table.bonusRevenueType', filterType: 'contains' },
    // Ekip — enrichment'ta row.user.team.name mevcut. Ayri bir team_name
    // flatten field'i yoksa DataTable auto-generate icin dot-notation
    // (row.user.team.name) desteklemiyorsa page-side taseronu duser.
    { field: 'team_name', label: 'userRevenueEntries.filters.team', filterType: 'contains' },
    // Kisi filtresi (Ibrahim 2026-10-26: "primlerde kisi filtresi").
    // CiroEklePage kullanicinin yalniz kendi kayitlarini gosterdigi icin
    // orada elenir.
    { field: 'user_name', label: 'userRevenueEntries.table.person', filterType: 'contains' },
  ],
  ciroAktar: [
    {
      field: 'yil',
      label: 'ciroAktar.year',
      filterType: 'exact',
      options: [],
    },
    {
      field: 'userTeamId',
      label: 'ciroAktar.filters.team',
      filterType: 'multiselect',
      options: [],
    },
    { field: 'personName', label: 'ciroAktar.columns.person', filterType: 'contains' },
  ],

  revenueCalculationRules: [
        { field: 'company_name', label: 'table.filters.company', filterType: 'contains', showIfRoot: true },
    {
      field: 'status',
      label: 'revenueCalculationRules.table.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'revenueCalculationRules.form.statusActive' },
        { value: 'inactive', labelKey: 'revenueCalculationRules.form.statusInactive' },
      ],
    },
    { field: 'jobTypeName', label: 'revenueCalculationRules.table.jobType', filterType: 'contains' },
    { field: 'revenueChannelText', label: 'revenueCalculationRules.table.revenueChannel', filterType: 'contains' },
  ],

  // Doküman Bankası — server-side filter yok (useDocumentBank paginated
  // degil), sadece client-side eslesme yapiliyor. `daterange` filter'i
  // updated_at uzerinde calisir; `contains` filter'lari transformData-siz
  // olarak dogrudan row alanlarini tarar.
  documentBank: [
    {
      field: 'updated_at',
      label: 'documentBank.filters.updatedAt',
      filterType: 'daterange',
    },
    {
      // Dosya tipi — enrichment'ta fileTypeLabel (PDF/DOC/XLS/VIDEO/IMG/FILE)
      // flatten ediliyor; multiselect static options ile filter.
      field: 'fileTypeLabel',
      label: 'documentBank.filters.fileType',
      filterType: 'multiselect',
      options: [
        { value: 'PDF', label: 'PDF' },
        { value: 'DOC', label: 'Word' },
        { value: 'XLS', label: 'Excel' },
        { value: 'VIDEO', label: 'Video' },
        { value: 'IMG', label: 'Görsel' },
        { value: 'FILE', label: 'Diğer' },
      ],
    },
    {
      field: 'category',
      label: 'documentBank.filters.category',
      filterType: 'multiselect',
      options: [
        { value: 'TEKNIK', label: 'Teknik' },
        { value: 'MALI', label: 'Mali' },
        { value: 'SATIS', label: 'Satış' },
      ],
    },
    {
      field: 'process',
      label: 'documentBank.filters.process',
      filterType: 'multiselect',
      options: [
        { value: 'ONAY_SONRASI', label: 'Onay sonrası' },
        { value: 'YAZIM', label: 'Yazım' },
        { value: 'DIGER', label: 'Diğer' },
      ],
    },
    {
      // Etiketler — 'docTagIds' bilinerek 'tags' YERINE kullanildi:
      // DataTableContainer 'tags' field'i icin useTags() (job etiketleri) ile
      // ozel effect calistiriyor; documentBank etiketleri ayri tabloda oldugu
      // icin bu override yanlis label uretiyordu. Standart multiselect array
      // matching: row.docTagIds = [1501, 1507] icin filter value = [1501]
      // seciliyorsa `value.some(v => docTagIds.includes(v))` true. Options
      // customFilterConfigs uzerinden runtime dolduruluyor.
      field: 'docTagIds',
      label: 'documentBank.filters.tags',
      filterType: 'multiselect',
      options: [],
    },
    {
      // Aktif/Pasif — leaveTypes/partners pattern'i: hook enrichment'ta
      // row.status = 'active' | 'inactive' string cast. Standart `exact`
      // multiselect filter (herhangi bir custom matcher gerektirmez).
      field: 'status',
      label: 'documentBank.filters.status',
      filterType: 'exact',
      options: [
        { value: 'active', labelKey: 'documentBank.status.active' },
        { value: 'inactive', labelKey: 'documentBank.status.passive' },
      ],
    },
  ],
};

export const ROW_ACTIONS = {
  firms: (row, permissions = null) => {
    const actions = [];

    // Add Communication cards action (no permission required)
    actions.push({
      type: 'firm-cards',
      label: 'firms.actions.firmCards',
      icon: 'groups',
      color: 'success',
    });

    // Add standard actions based on permissions
    if (!permissions || permissions.canEdit) {
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
      });
    }

    if (!permissions || permissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
      });
    }

    return actions;
  },

  offers: (row, permissions = null) => {
    const actions = [];

    // Add standard actions based on permissions
    if (!permissions || permissions.canEdit) {
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
      });
    }

    if (!permissions || permissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
      });
    }

    return actions;
  },

  jobs: (row, permissions = null) => {
    const actions = [];

    if (row.contract) {
      actions.push({
        type: 'viewContract',
        label: 'jobs.viewContract',
        icon: 'visibility',
        color: 'success',
      });
    }

    if (row.job_assignment_id) {
      actions.push({
        type: 'viewJobAssignment',
        label: 'jobAssignments.viewAssignment',
        icon: 'assignment_ind',
        color: 'info',
      });
    }

    // Add update status action
    actions.push({
      type: 'updateStatus',
      label: 'jobs.updateStatus',
      icon: 'update',
      color: 'warning',
    });

    // İş ONAYLANDI/TAMAMLANDI ise plan oluşturulabilir. Ayrıca — iş sonradan
    // sonlandırılsa (DANISMANLIK_SONLANDIRILDI, IPTAL, KAPATILDI vs.) bile,
    // daha önce girilmiş rapor planı takip için görünmeye devam etmeli.
    const hasPlannedReports = Array.isArray(row.job_reports) && row.job_reports.length > 0;
    if (row.job_status === 'ONAYLANDI' || row.job_status === 'TAMAMLANDI' || hasPlannedReports) {
      actions.push({
        type: 'updateReportingPlan',
        label: 'jobs.updateReportingPlan',
        icon: 'edit_note',
        color: 'primary',
      });
    }

    // Add standard actions based on permissions
    if (!permissions || permissions.canEdit) {
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
      });
    }

    if (!permissions || permissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
      });
    }

    return actions;
  },

  invoices: (row, permissions = null) => {
    const actions = [];

    if (row.contract) {
      actions.push({
        type: 'viewContract',
        label: 'invoices.viewContract',
        icon: 'visibility',
        color: 'success',
      });
    }

    if (row.job) {
      actions.push({
        type: 'viewJobStatus',
        label: 'jobs.viewStatus',
        icon: 'bar_chart',
        color: 'success',
      });
    }

    if (!permissions || permissions.canEdit) {
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
      });
    }

    if (!permissions || permissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
      });
    }

    return actions;
  },

  collections: () => [
    {
      type: 'manage',
      label: 'tahsilat.manage',
      icon: 'manage_accounts',
      color: 'primary',
    },
  ],

  contracts: (row, permissions = null) => {
    const actions = [];

    // Sözleşmeyi yenile: contract_renewals tablosuna yeni kayıt ekler.
    if (!permissions || permissions.canEdit) {
      actions.push({
        type: 'renewContract',
        label: 'contracts.actions.renew',
        icon: 'autorenew',
        color: 'info',
      });
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
      });
    }

    if (!permissions || permissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
      });
    }

    return actions;
  },

  // Doküman Bankası satir aksiyonlari. Sirasi ile: İndir (herkes),
  // Versiyonlar (herkes), Yeni Versiyon (yonetici), Atamalar (yonetici),
  // Düzenle/Sil (yonetici — DynamicCrudPage default). Yeni Versiyon /
  // Atamalar kendi `type`'lariyla page'in `onRowAction`'ina duser.
  documentBank: (row, permissions = null) => {
    const actions = [];
    const hasVersion = Array.isArray(row?.versions) && row.versions.length > 0;

    if (hasVersion) {
      actions.push({
        type: 'downloadLatest',
        label: 'documentBank.actions.download',
        icon: 'download',
        color: 'success',
      });
    }

    actions.push({
      type: 'viewVersions',
      label: 'documentBank.actions.viewVersions',
      icon: 'history',
      color: 'info',
    });

    if (!permissions || permissions.canEdit) {
      actions.push({
        type: 'newVersion',
        label: 'documentBank.actions.update',
        icon: 'upgrade',
        color: 'warning',
      });
      actions.push({
        type: 'manageAssignments',
        label: 'documentBank.actions.manageAssignments',
        icon: 'group',
        color: 'primary',
      });
      actions.push({
        type: 'edit',
        label: 'table.actions.edit',
      });
    }

    if (!permissions || permissions.canDelete) {
      actions.push({
        type: 'delete',
        label: 'table.actions.delete',
      });
    }

    return actions;
  },
};

export const SEARCH_CONFIGS = {
  users: {
    placeholder: 'table.search.placeholderUsers',
    // Use raw column / relation paths so serverFilters mode can build a
    // valid Hasura where. Transformed `firstName`/`lastName` etc. (added
    // by useUsers.transformData) match the dotted paths via the response
    // shape, so client-side fallback still works for non-server tables.
    fields: ['first_name', 'last_name', 'email', 'company.name', 'title.title_name'],
  },

  groups: {
    placeholder: 'table.search.placeholderGroups',
    // managerName is a JS-side first_name+last_name concat — search the
    // underlying user.first_name / user.last_name columns directly.
    // companyName maps to company.name.
    fields: [
      'name',
      'description',
      'user.first_name',
      'user.last_name',
      'company.name',
    ],
  },

  companies: {
    placeholder: 'table.search.placeholder',
    fields: [
      'name',
      'name_of_representative_',
      'email_address_of_representative',
    ],
  },

  teams: {
    placeholder: 'table.search.placeholderTeams',
    // managerName/companyName/groupName are JS-derived in transformData —
    // map to underlying Hasura columns/relations.
    fields: [
      'name',
      'description',
      'user.first_name',
      'user.last_name',
      'company.name',
      'group.name',
    ],
  },

  teamMembers: {
    placeholder: 'table.search.placeholderMembers',
    fields: ['firstName', 'lastName', 'email', 'phoneNumber', 'titleName'],
  },

  teamFirms: {
    placeholder: 'teamFirms.searchFirm',
    fields: ['firm_name', 'firm_short_name', 'firm_city'],
  },

  userFirms: {
    placeholder: 'userFirms.searchFirm',
    fields: ['firm_name', 'firm_short_name', 'firm_city'],
  },

  titles: {
    placeholder: 'table.search.placeholderTitles',
    fields: ['title_name', 'title_code', 'title_description'],
  },

  leaveTypes: {
    placeholder: 'leaveTypes.search.placeholder',
    fields: ['name', 'description'],
  },

  partners: {
    placeholder: 'partners.search.placeholder',
    fields: ['name', 'description'],
  },

  sectors: {
    placeholder: 'sectors.search.placeholder',
    fields: ['name', 'description'],
  },

  firms: {
    placeholder: 'firms.search.placeholder',
    fields: ['firm_name', 'firm_short_name', 'firm_city', 'description'],
  },

  // NOTE: a second, more complete `jobs` definition appears later in this
  // object and would silently override this one (last-wins). Removing this
  // stub avoids confusion; the canonical search config is below.
  firm_cards: {
    placeholder: 'firms.firmCards.search.placeholder',
    fields: [
      'firm_cart_firstname',
      'firm_cart_lastname',
      'firm_cart_mail',
      'firm_cart_phone',
      'firm_cart_title',
    ],
  },

  expenseTypes: {
    placeholder: 'expenseTypes.search.placeholder',
    fields: ['name', 'description'],
  },
  invoiceCodes: {
    placeholder: 'invoiceCodes.search.placeholder',
    fields: ['code_name', 'product_name', 'description', 'company.name'],
  },
  expenses: {
    placeholder: 'expenses.search.placeholder',
    // Used both client-side (against transformed rows) and translated to
    // Hasura paths in serverFilters mode. Only real columns / relation paths
    // belong here; transformed display fields are reached via the dotted
    // forms below.
    fields: [
      'description',
      'expense_type.expense_type_name',
      'user.first_name',
      'user.last_name',
    ],
  },
  leaves: {
    placeholder: 'leaves.search.placeholder',
    fields: [
      'leaveType.leave_type_name',
      'leave_request_description',
      'status',
    ],
  },
  leavesRequest: {
    placeholder: 'leaves.search.placeholder',
    fields: [
      'user.first_name',
      'user.last_name',
      'user.email',
    ],
  },

  contractSubjects: {
    placeholder: 'contractSubjects.search.placeholder',
    fields: ['name', 'description'],
  },

  jobTypes: {
    placeholder: 'jobTypes.search.placeholder',
    fields: ['name', 'description'],
  },

  scopeOfWork: {
    placeholder: 'scopeOfWork.search.placeholder',
    fields: ['name', 'description'],
  },

  pricingMethods: {
    placeholder: 'pricingMethods.search.placeholder',
    fields: ['name', 'description'],
  },

  workStatus: {
    placeholder: 'workStatus.search.placeholder',
    fields: ['name', 'description'],
  },

  reportTypes: {
    placeholder: 'reportTypes.search.placeholder',
    fields: ['name', 'description'],
  },

  tasks: {
    placeholder: 'tasks.search.placeholder',
    // taskTitle/taskDescription/firmName are JS-side aliases produced by
    // useTasks.transformData — translate to actual Hasura columns/relations.
    fields: ['task_title', 'task_description', 'firm.firm_name'],
  },

  contracts: {
    placeholder: 'contracts.search.placeholder',
    fields: [
      'contract_subject.name',
      'scope_of_work.name',
      'pricing_method.name',
      'work_status.name',
      'firm.firm_name',
      'firm.firm_short_name',
    ],
  },

  proposal_status: {
    placeholder: 'proposalStatuses.search.placeholder',
    fields: [
      'name',
      'description',
      'company_name',
    ],
  },

  offers: {
    placeholder: 'offers.search.placeholder',
    fields: [
      'firm.firm_name',
      'firm.firm_short_name',
      'contract_subject.name',
      'scope_of_work.name',
    ],
  },

  jobs: {
    placeholder: 'jobs.search.placeholder',
    // Field names are also used as paths in serverFilters mode (see
    // utils/buildHasuraWhere.js) — only real Hasura columns / array_relation
    // paths should appear here. The transformed `firm_name`/`description`
    // values added by useJobs.transformData are covered by the dotted paths
    // below, so we don't list them as flat fields.
    //
    // Teknik/mali sorumlu isim uzerinden de search ediliyor: bir isin
    // sorumlusu Edanur ise "edanur" yazdiginda satir cikacak. Onceden yalnizca
    // olusturan (user.first_name/last_name) tarandigi icin sorumlu-atanmis
    // isler search'ten kaciyordu (Is Dagilimlari sayfasindaki sayilarla fark).
    fields: [
      'job_title',
      'description_of_job',
      { field: 'project_number', type: 'int' },
      'firm_short_name',
      'company.name',
      'user.first_name',
      'user.last_name',
      'contract.contract_subject.name',
      'contract.firm.firm_name',
      'contract.firm.firm_short_name',
      // Sorumlu atamalari — job_assignment (tekil object relationship).
      'job_assignment.technical_user.first_name',
      'job_assignment.technical_user.last_name',
      'job_assignment.financial_user.first_name',
      'job_assignment.financial_user.last_name',
    ],
  },

  tags: {
    placeholder: 'tags.search.placeholder',
    fields: ['name', 'tags_color', 'tags_page'],
  },

  documentBankTags: {
    placeholder: 'documentBankTags.search.placeholder',
    fields: ['name', 'color'],
  },

  jobAssignments: {
    placeholder: 'jobAssignments.search.placeholder',
    fields: [
      // Backend search runs against the actual Hasura columns. Display-only
      // derived fields (jobTitle/firmName/etc. produced by transformData)
      // map to dotted relation paths. project_number is INTEGER → typed.
      { field: 'jobTitle', hasuraField: 'job.job_title' },
      { field: 'firmName', hasuraField: 'job.firm.firm_name' },
      { field: 'projectNumber', hasuraField: 'job.project_number', type: 'int' },
      { field: 'companyName', hasuraField: 'company.name' },
      { field: 'assignedByUser', hasuraField: 'assigned_by_user.first_name' },
    ],
  },
  paymentPlanTemplates: {
    fields: [
      'templateName',
      'description',
      'year',
      'companyName',
      'status',
      'created_at'
    ],
  },
  pricingProcess: {
    fields: [
      'processName',
      'description',
      'processType',
      'companyName',
      'status',
      'created_at'
    ],
  },
  invoices: {
    placeholder: 'invoices.search.placeholder',
    // contractInfo.* is a JS-side nested object built by useInvoices —
    // translate to the real contract.firm.* relation paths for server search.
    fields: [
      'company.name',
      'invoice_name',
      'contract.firm.firm_name',
      'contract.firm.firm_short_name',
    ],
  },

  collections: {
    placeholder: 'tahsilat.search',
    // Used both client-side and translated to Hasura paths in serverFilters
    // mode. `firm_name`/`firm_short_name` live on the `firm` relation; `note`
    // is not a column on collections so it's omitted from the backend search.
    // The flat `firm_name` / `firm_short_name` keys added by transformData
    // still let client-side search work in the Reminders / PaymentPromises
    // tabs (which use their own SEARCH_CONFIGS entries).
    fields: ['firm.firm_name', 'firm.firm_short_name', 'luca_code'],
  },

  reminders: {
    placeholder: 'tahsilat.reminderSearch',
    fields: ['firm_name', 'firm_short_name', 'note'],
  },

  paymentPromises: {
    placeholder: 'tahsilat.promiseSearch',
    fields: ['firm_name', 'firm_short_name', 'promised_by', 'note'],
  },

  reportManagement: {
    placeholder: 'reportManagement.filters.search',
    fields: ['firmName', 'jobTitle', 'reportTypeName', 'projectNumber'],
  },

  // ─── Prim Yönetimi ──────────────────────────────────────────────────────
  bonusRevenueTypes: {
    placeholder: 'bonusRevenueTypes.search.placeholder',
    fields: ['name', 'code'],
  },
  bonusRevenueRules: {
    placeholder: 'bonusRevenueRules.search.placeholder',
    fields: ['operation', 'aciklama'],
  },
  bonusRules: {
    placeholder: 'bonusRules.search.placeholder',
    fields: ['aciklama'],
  },
  bonusPayments: {
    placeholder: 'bonusPayments.search.placeholder',
    fields: ['aciklama', 'user.first_name', 'user.last_name'],
  },
  userBonusCoefficients: {
    placeholder: 'userBonusCoefficients.search.placeholder',
    fields: ['user.first_name', 'user.last_name'],
  },
  bonusTargets: {
    placeholder: 'bonusTargets.search.placeholder',
    fields: ['aciklama'],
  },
  userRevenueEntries: {
    placeholder: 'userRevenueEntries.search.placeholder',
    // job.project_number nested — DataTableContainer.getValue dot notation
    // desteklior; transformed top-level `project_number` sadece TUBITAK icin
    // dolu oldugundan job.project_number ile tum is turleri icin substring
    // eslesme yapabiliyoruz.
    fields: ['aciklama', 'firm_name', 'referans', 'ciro_kanali_label', 'job.project_number'],
  },

  ciroAktar: {
    placeholder: 'table.search.placeholder',
    fields: ['personName', 'firmName', 'jobLabel'],
  },

  revenueCalculationRules: {
    placeholder: 'revenueCalculationRules.search.placeholder',
    fields: ['jobTypeName', 'revenueChannelText', 'feeSourceText', 'description'],
  },

  documentBank: {
    placeholder: 'documentBank.search.placeholder',
    // useDocumentBank paginated degil — sadece client-side; row alanlari
    // dogrudan document_bank kolonlari (title, description).
    fields: ['title', 'description'],
  },
};
